/* eslint-disable */
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Typography, Button, Box, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Select, MenuItem, FormControl,
  InputLabel, RadioGroup, FormControlLabel, Radio, Chip,
  ListItemText, Checkbox, List, ListItem, IconButton, Tabs, Tab,
  Grid, Paper, FormGroup, Popper, ClickAwayListener, Fade,
  Tooltip, Badge, Collapse, Alert,
} from "@mui/material";
import { fetcher, patchFetcher, postFetcher } from "src/utils/axios";
import { enqueueSnackbar } from "notistack";
import Iconify from "src/components/iconify";
import OutputDeleteConfirmationDialog from "./OutputDeleteConfirmationDialog ";
import DeleteConfirmationInsideEdit from "./DeleteConfirmationInsideEdit";

// ─── Design tokens ────────────────────────────────────────────────────────────
const c = {
  bg: "#F8FAFC",
  surface: "#FFFFFF",
  surfaceHover: "#FAFAFA",
  border: "#E2E8F0",
  borderFocus: "#6366F1",
  text: "#0F172A",
  textSecondary: "#64748B",
  textMuted: "#A1A1AA",
  accent: "#18181B",
  accentHover: "#3F3F46",
  accentLight: "#F4F4F5",
  success: "#16A34A",
  successBg: "#F0FDF4",
  successBorder: "#BBF7D0",
  error: "#DC2626",
  errorBg: "#FEF2F2",
  warning: "#D97706",
  warningBg: "#FFFBEB",
  blue: "#3B82F6",
  blueBg: "#F0F9FF",
  blueBorder: "#BAE6FD",
  purple: "#8B5CF6",
  purpleBg: "#F5F3FF",
  purpleBorder: "#EDE9FE",
  chipBg: "#F4F4F5",
  chipBorder: "#E4E4E7",
};

const sectionStyle = {
  background: c.surface,
  border: `1.5px solid ${c.border}`,
  borderRadius: "16px",
  p: 3,
  mb: 2,
  boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": { borderColor: "#CBD5E1", boxShadow: "0 4px 12px rgba(0,0,0,0.04)" },
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    background: c.surface,
    fontSize: "14px",
    transition: "all 0.2s",
    "& fieldset": { borderColor: c.border },
    "&:hover fieldset": { borderColor: "#94A3B8" },
    "&.Mui-focused fieldset": { borderColor: c.borderFocus, borderWidth: "2px" },
    "&.Mui-focused": { boxShadow: "0 0 0 4px rgba(99,102,241,0.1)" },
  },
  "& .MuiInputLabel-root": { fontSize: "14px", color: c.textSecondary, "&.Mui-focused": { color: c.borderFocus } },
};

// ─── Formula token types with display configs ────────────────────────────────
// Add function style to TOKEN_STYLES
const TOKEN_STYLES = {
  material: { bg: "#F1F5F9", border: "#CBD5E1", color: "#475569", icon: "📦" },
  final_product: { bg: "#FAF5FF", border: "#E9D5FF", color: "#7E22CE", icon: "💎" },
  output: { bg: "#F0F9FF", border: "#BAE6FD", color: "#0369A1", icon: "🔗" },
  transform: { bg: "#FFF7ED", border: "#FFEDD5", color: "#9A3412", icon: "⚡" },
  operator: { bg: "#FEF3C7", border: "#FDE68A", color: "#92400E", icon: "" },
  number: { bg: "#F8FAFC", border: "#E2E8F0", color: "#334155", icon: "" },
  function: { bg: "#EFF6FF", border: "#BFDBFE", color: "#1E40AF", icon: "ƒ" },
};

// Fix the LiveCalculationPreview component - remove the duplicate tokens definition
const LiveCalculationPreview = ({ formulaStr, attributesList, nodeOutputs, transformAttributes }) => {
  const [attributeValues, setAttributeValues] = useState({});
  const [calculationResult, setCalculationResult] = useState(null);
  const [error, setError] = useState(null);

  // Parse formula and evaluate with user-provided values
  const evaluateFormula = useCallback((formula, values) => {
    if (!formula?.trim()) return null;

    try {
      // Replace tokens with their values
      let evaluableFormula = formula;

      // First, identify material attributes and node outputs and protect them
      const placeholders = {};
      let placeholderIndex = 0;

      // Protect selected transform attributes (t_instanceKey_productId_attr)
      // Convert them to their material attribute equivalent for evaluation
      evaluableFormula = evaluableFormula.replace(/t_\d+_(\d+)_(\w+)/g, (match, productId, attr) => {
        const key = `${productId}.${attr}`;
        const value = values[key];
        if (value !== undefined && value !== '') {
          const placeholder = `__PLACEHOLDER_${placeholderIndex++}__`;
          placeholders[placeholder] = value;
          return placeholder;
        }
        return match;
      });

      // Protect material attributes (matId.attr)
      evaluableFormula = evaluableFormula.replace(/(\d+)\.(\w+)/g, (match, matId, attr) => {
        const key = `${matId}.${attr}`;
        const value = values[key];
        if (value !== undefined && value !== '') {
          const placeholder = `__PLACEHOLDER_${placeholderIndex++}__`;
          placeholders[placeholder] = value;
          return placeholder;
        }
        return match;
      });

      // Protect node outputs (o.id)
      evaluableFormula = evaluableFormula.replace(/o\.(\d+)/g, (match, outputId) => {
        const key = `o.${outputId}`;
        const value = values[key];
        if (value !== undefined && value !== '') {
          const placeholder = `__PLACEHOLDER_${placeholderIndex++}__`;
          placeholders[placeholder] = value;
          return placeholder;
        }
        return match;
      });

      // Protect transform attributes (t.id.attr)
      evaluableFormula = evaluableFormula.replace(/t\.(\d+)\.(\w+)/g, (match, id, attr) => {
        const key = `t.${id}.${attr}`;
        const value = values[key];
        if (value !== undefined && value !== '') {
          const placeholder = `__PLACEHOLDER_${placeholderIndex++}__`;
          placeholders[placeholder] = value;
          return placeholder;
        }
        return match;
      });

      // Define helper functions for evaluation
      const mathFunctions = {
        MAX: (...args) => Math.max(...args),
        MIN: (...args) => Math.min(...args),
        AVG: (...args) => args.reduce((a, b) => a + b, 0) / args.length,
        SUM: (...args) => args.reduce((a, b) => a + b, 0),
        ROUND: (val) => Math.round(val),
        ABS: (val) => Math.abs(val),
        POW: (base, exp) => Math.pow(base, exp),
        SQRT: (val) => Math.sqrt(val),
      };

      // Replace function calls with JavaScript function calls
      Object.keys(mathFunctions).forEach(funcName => {
        const regex = new RegExp(`${funcName}\\(([^)]+)\\)`, 'g');
        evaluableFormula = evaluableFormula.replace(regex, (match, args) => {
          const parsedArgs = args.split(',').map(arg => parseFloat(arg.trim()));
          return mathFunctions[funcName](...parsedArgs);
        });
      });

      // Restore placeholders
      Object.keys(placeholders).forEach(placeholder => {
        evaluableFormula = evaluableFormula.replace(placeholder, placeholders[placeholder]);
      });

      // Safe evaluation using Function constructor
      const result = Function('"use strict"; return (' + evaluableFormula + ')')();
      return isNaN(result) ? null : result;
    } catch (err) {
      console.error('Evaluation error:', err);
      return null;
    }
  }, []);

  // Extract all unique tokens from formula - THIS IS THE CORRECT ONE
  const tokens = useMemo(() => {
    if (!formulaStr?.trim()) return [];

    const tokensSet = new Set();
    const materialRegex = /(\d+)\.(\w+)/g;
    const nodeRegex = /o\.(\d+)/g;
    const transformRegex = /t\.(\d+)\.(\w+)/g;
    const selectedTransformRegex = /t_\d+_(\d+)_(\w+)/g; // New format: t_instanceKey_productId_attribute

    let match;
    while ((match = materialRegex.exec(formulaStr)) !== null) {
      tokensSet.add(`${match[1]}.${match[2]}`);
    }
    while ((match = nodeRegex.exec(formulaStr)) !== null) {
      tokensSet.add(`o.${match[1]}`);
    }
    while ((match = transformRegex.exec(formulaStr)) !== null) {
      tokensSet.add(`t.${match[1]}.${match[2]}`);
    }
    while ((match = selectedTransformRegex.exec(formulaStr)) !== null) {
      // For selected transforms, add the productId.attribute as a material attribute
      tokensSet.add(`${match[1]}.${match[2]}`);
    }

    return Array.from(tokensSet);
  }, [formulaStr]);

  // Get token display info
  const getTokenInfo = useCallback((token) => {
    // Handle selected transform attributes (new format: t_instanceKey_productId_attribute)
    if (token.match(/^t_\d+_\d+_\w+$/)) {
      const parts = token.split('_');
      const productId = parseInt(parts[2]);
      const attr = parts[3];
      const material = attributesList?.find(m => m.id === productId);
      return {
        type: 'material',
        label: material ? `${material.name}: ${attr} (transformed)` : token,
        category: 'Material Attribute',
        placeholder: `Enter ${attr}`,
      };
    }

    if (token.match(/^\d+\.\w+$/)) {
      const [matId, attr] = token.split('.');
      const material = attributesList?.find(m => m.id === parseInt(matId));
      return {
        type: 'material',
        label: material ? `${material.name}: ${attr}` : token,
        category: 'Material Attribute',
        placeholder: `Enter ${attr}`,
      };
    }
    if (token.match(/^o\.\d+$/)) {
      const outputId = parseInt(token.split('.')[1]);
      const output = nodeOutputs?.flatMap(n => n.outputs).find(o => o.id === outputId);
      return {
        type: 'output',
        label: output ? `${output.nodeName}: ${output.name}` : token,
        category: 'Node Output',
        placeholder: `Enter value`,
      };
    }
    if (token.match(/^t\.\d+\.\w+$/)) {
      const [, id, attr] = token.split('.');
      const transform = transformAttributes?.flatMap(n => n.transformations).find(t => t.output_id === parseInt(id) && t.attribute === attr);
      return {
        type: 'transform',
        label: transform?.display_name || token,
        category: 'Transform Attribute',
        placeholder: `Enter value`,
      };
    }
    return null;
  }, [attributesList, nodeOutputs, transformAttributes]);

  // Update calculation when values change
  useEffect(() => {
    const result = evaluateFormula(formulaStr, attributeValues);
    setCalculationResult(result);
    setError(result === null && formulaStr.trim() ? 'Invalid formula or missing values' : null);
  }, [formulaStr, attributeValues, evaluateFormula]);

  // Handle value change
  const handleValueChange = (token, value) => {
    setAttributeValues(prev => ({
      ...prev,
      [token]: value === '' ? undefined : parseFloat(value)
    }));
  };

  if (!formulaStr?.trim()) return null;

  return (
    <Box sx={{
      mt: 2,
      border: `1.5px solid ${c.border}`,
      borderRadius: "16px",
      overflow: "hidden",
      background: c.surface,
      boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)",
    }}>
      {/* Header */}
      <Box sx={{
        px: 2.5,
        py: 1.5,
        borderBottom: `1px solid ${c.border}`,
        background: c.bg,
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}>
        <Iconify icon="solar:calculator-bold" width={18} sx={{ color: c.accent }} />
        <Typography sx={{
          fontSize: "11px",
          fontWeight: 700,
          letterSpacing: "0.08em",
          color: c.textSecondary,
          textTransform: "uppercase",
        }}>
          Live Calculation Preview
        </Typography>
        <Typography sx={{
          fontSize: "10px",
          color: c.textMuted,
          ml: "auto",
        }}>
          Enter values to see real-time results
        </Typography>
      </Box>

      <Box sx={{ p: 2.5 }}>
        {/* Input fields for tokens */}
        {tokens.length > 0 && (
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{
              fontSize: "10px",
              fontWeight: 700,
              color: c.textMuted,
              mb: 1.5,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}>
              Input Values
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {tokens.map(token => {
                const info = getTokenInfo(token);
                if (!info) return null;

                return (
                  <Box key={token} sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 1.5,
                    borderRadius: "12px",
                    background: `${c.border}08`,
                    transition: "all 0.2s",
                    "&:hover": {
                      background: `${c.border}15`,
                    }
                  }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: c.text,
                        mb: 0.25,
                      }}>
                        {info.label}
                      </Typography>
                      <Typography sx={{
                        fontSize: "10px",
                        color: c.textMuted,
                        fontFamily: "monospace",
                      }}>
                        {token}
                      </Typography>
                    </Box>
                    <Box sx={{ width: 120 }}>
                      <TextField
                        size="small"
                        type="number"
                        value={attributeValues[token] ?? ''}
                        onChange={(e) => handleValueChange(token, e.target.value)}
                        placeholder={info.placeholder}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            fontSize: "13px",
                            "& fieldset": { borderColor: c.border },
                            "&:hover fieldset": { borderColor: c.accent },
                            "&.Mui-focused fieldset": { borderColor: c.accent, borderWidth: "2px" },
                          },
                        }}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* Result display */}
        <Box sx={{
          mt: tokens.length > 0 ? 2 : 0,
          p: 2,
          borderRadius: "12px",
          background: calculationResult !== null ? `${c.successBg}80` : `${c.errorBg}80`,
          border: `1px solid ${calculationResult !== null ? c.successBorder : c.error}`,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography sx={{
              fontSize: "10px",
              fontWeight: 700,
              color: c.textMuted,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}>
              Calculated Result
            </Typography>
            {calculationResult !== null && (
              <Typography sx={{
                fontSize: "10px",
                color: c.success,
                fontWeight: 600,
              }}>
                ✓ Real-time
              </Typography>
            )}
          </Box>

          {error ? (
            <Typography sx={{
              fontSize: "12px",
              color: c.error,
              fontFamily: "monospace",
            }}>
              {error}
            </Typography>
          ) : calculationResult !== null ? (
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
              <Typography sx={{
                fontSize: "24px",
                fontWeight: 800,
                color: c.accent,
                fontFamily: "monospace",
              }}>
                {calculationResult.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 4 })}
              </Typography>
              <Typography sx={{ fontSize: "12px", color: c.textSecondary }}>
                (evaluated value)
              </Typography>
            </Box>
          ) : (
            <Typography sx={{
              fontSize: "12px",
              color: c.textMuted,
              fontStyle: "italic",
            }}>
              {tokens.length > 0 ? 'Enter values above to see calculation result' : 'No variables to evaluate'}
            </Typography>
          )}
        </Box>

        {/* Formula display */}
        <Box sx={{
          mt: 2,
          p: 1.5,
          borderRadius: "10px",
          background: c.bg,
          border: `1px dashed ${c.border}`,
        }}>
          <Typography sx={{
            fontSize: "10px",
            fontWeight: 700,
            color: c.textMuted,
            mb: 0.75,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
          }}>
            Current Formula
          </Typography>
          <Typography sx={{
            fontSize: "11px",
            color: c.textSecondary,
            fontFamily: "monospace",
            wordBreak: "break-all",
          }}>
            {formulaStr}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// TransformationSection.jsx - Updated to support option-based transformations
const TransformationSection = React.memo(({
  nodeType,
  allMaterialAttributes,
  needsTransformation,
  selectedTransformAttributes,
  transformOperators,
  transformUserValues,
  onTransformationChange,
  onAttributeToggle,
  onOperatorChange,
  onValueChange,
  index,
  optionId = null, // Add optionId to support multiple options
  optionLabel = null // Optional label for display
}) => {
  if (nodeType === "diamond" || allMaterialAttributes.length === 0) return null;

  // Create a unique key for this transformation instance
  const instanceKey = optionId ? `${index}_${optionId}` : `${index}`;

  return (
    <Box sx={{
      background: c.surface,
      border: `1.5px solid ${c.border}`,
      borderRadius: "20px",
      overflow: "hidden",
      flexShrink: 0,
      boxShadow: "0 8px 16px -6px rgba(0,0,0,0.08)",
      mb: 2 // Add margin bottom for spacing when multiple options
    }}>
      <Box sx={{
        px: 2.5,
        py: 2,
        borderBottom: `1px solid ${c.border}`,
        background: c.bg,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "2px", background: `linear-gradient(135deg, #C2410C, #F97316)` }} />
          <Typography sx={{ fontSize: "12px", fontWeight: 800, color: c.textSecondary }}>
            Transformation {optionLabel ? `for ${optionLabel}` : ""}
          </Typography>
        </Box>
        <RadioGroup
          row
          value={needsTransformation?.[instanceKey] || "no"}
          onChange={(e) => onTransformationChange(instanceKey, e.target.value)}
          sx={{ m: 0 }}
        >
          <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" sx={{ mr: 1.5 }} />
          <FormControlLabel value="no" control={<Radio size="small" />} label="No" sx={{ mr: 0 }} />
        </RadioGroup>
      </Box>
      <Collapse in={needsTransformation?.[instanceKey] === "yes"}>
        <Box sx={{ p: 2.5 }}>
          {/* Attribute checkboxes */}
          <Typography sx={{ fontSize: "10px", fontWeight: 800, color: c.textMuted, mb: 1.5 }}>
            📋 Select Attributes
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 2 }}>
            {allMaterialAttributes.map(attr => {
              const key = `${attr.productId}:${attr.attribute}`;
              const checked = selectedTransformAttributes?.[instanceKey]?.includes(key) || false;
              return (
                <Chip
                  key={key}
                  label={attr.displayName}
                  size="small"
                  onClick={() => onAttributeToggle(instanceKey, attr.productId, attr.attribute)}
                  sx={{
                    borderRadius: "8px",
                    background: checked ? c.accentLight : c.chipBg,
                    border: `1px solid ${checked ? c.accent : c.chipBorder}`,
                    color: checked ? c.accent : c.textSecondary,
                    fontSize: "11px",
                    cursor: "pointer",
                    height: 28,
                    "&:hover": {
                      background: c.accentLight,
                      borderColor: c.accent,
                    },
                    transition: "all 0.2s",
                  }}
                />
              );
            })}
          </Box>

          {/* Operator + value rows */}
          {(selectedTransformAttributes?.[instanceKey] || []).length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 110px 100px", gap: 1.5 }}>
                <Typography sx={{ fontSize: "10px", fontWeight: 700, color: c.textMuted }}>Attribute</Typography>
                <Typography sx={{ fontSize: "10px", fontWeight: 700, color: c.textMuted }}>Operator</Typography>
                <Typography sx={{ fontSize: "10px", fontWeight: 700, color: c.textMuted }}>Value</Typography>
              </Box>
              {(selectedTransformAttributes[instanceKey] || []).map(key => {
                const [pid, attr] = key.split(":");
                const info = allMaterialAttributes.find(a => a.productId === Number(pid) && a.attribute === attr);
                return (
                  <Box key={key} sx={{ display: "grid", gridTemplateColumns: "1fr 110px 100px", gap: 1.5, alignItems: "center" }}>
                    <Typography sx={{ fontSize: "12px", color: c.text, fontWeight: 600 }}>
                      {info?.attribute || key}
                    </Typography>
                    <FormControl size="small" fullWidth>
                      <Select
                        value={transformOperators?.[instanceKey]?.[key] || "+"}
                        onChange={e => onOperatorChange(instanceKey, key, e.target.value)}
                        sx={{ fontSize: "12px", borderRadius: "10px" }}
                      >
                        <MenuItem value="+">➕ Add</MenuItem>
                        <MenuItem value="-">➖ Subtract</MenuItem>
                        <MenuItem value="*">✖️ Multiply</MenuItem>
                        <MenuItem value="/">➗ Divide</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      type="number"
                      value={transformUserValues?.[instanceKey]?.[key] || ""}
                      onChange={e => onValueChange(instanceKey, key, e.target.value)}
                      placeholder="0"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          fontSize: "13px",
                        }
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
});

TransformationSection.displayName = 'TransformationSection';
// ─── FormulaBuilder: visual token-based formula editor ────────────────────────

// ─── FormulaBuilder: full inline text editor with autocomplete ────────────────────────

// ─── FormulaBuilder: full inline text editor with autocomplete ────────────────────────
const FormulaBuilder = ({ value, onChange, attributes, nodeOutputs, transformAttributes, connectedOutputAttributes, disabled, placeholder }) => {
  const [localValue, setLocalValue] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [showLivePreview, setShowLivePreview] = useState(false);
  const [formulaError, setFormulaError] = useState(null);
  const [validationWarning, setValidationWarning] = useState(null);
  const inputRef = useRef(null);
  const validationTimeoutRef = useRef(null);

  // Sync local value with prop value
  useEffect(() => {
    setLocalValue(value || "");
  }, [value]);

  // Comprehensive validation similar to LiveCalculationPreview
  // Comprehensive validation similar to LiveCalculationPreview
  const validateFormulaComprehensive = useCallback((formulaStr) => {
    if (!formulaStr?.trim()) {
      setFormulaError(null);
      setValidationWarning(null);
      return true;
    }

    try {
      const trimmedFormula = formulaStr.trim();

      // Check if formula contains any tokens (including new format: t_instanceKey_productId_attribute)
      const tokenRegex = /(t_\d+_\d+_\w+|t\.\d+\.\w+|o\.\d+|\d+\.\w+|-?\d+(?:\.\d+)?|[+\-*/()])/g;
      const tokens = trimmedFormula.match(tokenRegex) || [];

      if (tokens.length === 0) {
        setFormulaError("Invalid formula: No valid tokens found");
        setValidationWarning(null);
        return false;
      }

      // Check for consecutive values without operators (improved logic)
      let hasConsecutiveValues = false;
      let lastWasValue = false;
      let lastWasOperator = false;
      let valuePositions = [];

      for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i];
        const isValue = /^(t_\d+_\d+_\w+|t\.\d+\.\w+|o\.\d+|\d+\.\w+|-?\d+(?:\.\d+))$/.test(token);
        const isOperator = /^[+\-*/()]$/.test(token);
        const isOpenParen = token === '(';
        const isCloseParen = token === ')';

        // Track values
        if (isValue) {
          valuePositions.push(i);
        }

        // Check for consecutive values (value followed immediately by value)
        if (isValue && lastWasValue && !isOpenParen && !isCloseParen) {
          hasConsecutiveValues = true;
          break;
        }

        // Check for value followed by open parenthesis without operator (function call style)
        if (isValue && token === '(') {
          hasConsecutiveValues = true;
          break;
        }

        // Update flags
        if (isValue) {
          lastWasValue = true;
          lastWasOperator = false;
        } else if (isOperator) {
          lastWasValue = false;
          lastWasOperator = true;
        } else {
          lastWasValue = false;
          lastWasOperator = false;
        }
      }

      if (hasConsecutiveValues) {
        setFormulaError("Invalid formula: Missing operator between values/attributes");
        setValidationWarning(null);
        return false;
      }

      // Check for unbalanced parentheses
      let parenthesesCount = 0;
      for (const char of trimmedFormula) {
        if (char === '(') parenthesesCount++;
        if (char === ')') parenthesesCount--;
        if (parenthesesCount < 0) {
          setFormulaError("Invalid formula: Unmatched closing parenthesis");
          setValidationWarning(null);
          return false;
        }
      }
      if (parenthesesCount !== 0) {
        setFormulaError("Invalid formula: Unmatched opening parenthesis");
        setValidationWarning(null);
        return false;
      }

      // Check for consecutive operators
      const noSpaces = trimmedFormula.replace(/\s/g, '');
      const operatorPattern = /[+\-*/]{2,}/;
      if (operatorPattern.test(noSpaces)) {
        setFormulaError("Invalid formula: Consecutive operators detected");
        setValidationWarning(null);
        return false;
      }

      // Check for operators at start or end (except minus sign for negative numbers)
      if (/^[+\*/]/.test(trimmedFormula)) {
        setFormulaError("Invalid formula: Formula cannot start with an operator (except minus sign)");
        setValidationWarning(null);
        return false;
      }
      if (/[+\-*/]$/.test(trimmedFormula)) {
        setFormulaError("Invalid formula: Formula cannot end with an operator");
        setValidationWarning(null);
        return false;
      }

      // Check for empty parentheses
      if (/\(\s*\)/.test(trimmedFormula)) {
        setFormulaError("Invalid formula: Empty parentheses detected");
        setValidationWarning(null);
        return false;
      }

      // Validate each token format
      const validTokenPattern = /^(t_\d+_\d+_\w+|t\.\d+\.\w+|o\.\d+|\d+\.\w+|-?\d+(?:\.\d+)?|[+\-*/()])$/;
      const allTokens = trimmedFormula.match(tokenRegex) || [];

      for (const token of allTokens) {
        if (!validTokenPattern.test(token)) {
          setFormulaError(`Invalid token: ${token}`);
          setValidationWarning(null);
          return false;
        }
      }

      // Additional validation: Check for valid sequences (number followed by attribute without operator)
      // This is a more specific check for patterns like "80.GSM" (which is actually a valid token)
      // But we need to check if tokens are properly separated

      // Create a string without spaces to check for missing operators
      const withoutSpaces = trimmedFormula.replace(/\s/g, '');
      const tokensWithoutSpaces = withoutSpaces.match(/(t\.\d+\.\w+|o\.\d+|\d+\.\w+|-?\d+(?:\.\d+)?|[+\-*/()])/g) || [];

      // Check if there are any two value tokens adjacent in the string without spaces
      let adjacentValues = false;
      for (let i = 0; i < tokensWithoutSpaces.length - 1; i++) {
        const currentToken = tokensWithoutSpaces[i];
        const nextToken = tokensWithoutSpaces[i + 1];
        const isCurrentValue = /^(t\.\d+\.\w+|o\.\d+|\d+\.\w+|-?\d+(?:\.\d+))$/.test(currentToken);
        const isNextValue = /^(t\.\d+\.\w+|o\.\d+|\d+\.\w+|-?\d+(?:\.\d+))$/.test(nextToken);

        if (isCurrentValue && isNextValue) {
          adjacentValues = true;
          break;
        }
      }

      if (adjacentValues) {
        setFormulaError("Invalid formula: Missing operator between values/attributes");
        setValidationWarning(null);
        return false;
      }

      // Check for valid attribute references
      const attributeTokens = allTokens.filter(token => token.match(/^\d+\.\w+$/));
      const nodeTokens = allTokens.filter(token => token.match(/^o\.\d+$/));
      const transformTokens = allTokens.filter(token => token.match(/^t\.\d+\.\w+$/));

      let hasMissingAttributes = false;
      const missingAttributes = [];

      // Check material attributes
      if (attributes && attributes.length > 0) {
        for (const token of attributeTokens) {
          const [id, attr] = token.split('.');
          const material = attributes.find(m => m.id === parseInt(id));
          if (!material) {
            hasMissingAttributes = true;
            missingAttributes.push(`Material ID ${id} not found`);
          } else if (!material.attributes || !material.attributes.includes(attr)) {
            hasMissingAttributes = true;
            missingAttributes.push(`Attribute "${attr}" not found in ${material.name}`);
          }
        }
      }

      // Check node outputs
      if (nodeOutputs && nodeOutputs.length > 0 && nodeTokens.length > 0) {
        const allOutputs = nodeOutputs.flatMap(n => n.outputs);
        for (const token of nodeTokens) {
          const id = parseInt(token.split('.')[1]);
          if (!allOutputs.find(o => o.id === id)) {
            hasMissingAttributes = true;
            missingAttributes.push(`Node output with ID ${id} not found`);
          }
        }
      }

      // Check transform attributes
      if (transformAttributes && transformAttributes.length > 0 && transformTokens.length > 0) {
        const allTransforms = transformAttributes.flatMap(n => n.transformations);
        for (const token of transformTokens) {
          const [, id, attr] = token.split('.');
          if (!allTransforms.find(t => t.output_id === parseInt(id) && t.attribute === attr)) {
            hasMissingAttributes = true;
            missingAttributes.push(`Transform attribute ${token} not found`);
          }
        }
      }

      if (hasMissingAttributes) {
        setFormulaError(null);
        setValidationWarning(`Warning: ${missingAttributes.join(', ')}`);
        return true; // Still return true as it's a warning, not an error
      }

      setFormulaError(null);
      setValidationWarning(null);
      return true;
    } catch (error) {
      console.error("Validation error:", error);
      setFormulaError("Invalid formula syntax");
      setValidationWarning(null);
      return false;
    }
  }, [attributes, nodeOutputs, transformAttributes]);

  // Debounced validation
  const debouncedValidate = useCallback((formulaStr) => {
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }
    validationTimeoutRef.current = setTimeout(() => {
      validateFormulaComprehensive(formulaStr);
    }, 500);
  }, [validateFormulaComprehensive]);

  // Build auto-suggestion list (only attributes and outputs, no functions)
  const allSuggestions = useMemo(() => {
    const list = [];

    // Add material attributes
    (attributes || []).forEach(mat => {
      (mat.attributes || []).forEach(attr => {
        list.push({
          label: `${mat.name}: ${attr}`,
          token: `${mat.id}.${attr}`,
          type: "material",
          group: mat.name,
          icon: "📦",
          searchText: `${mat.name} ${attr} ${mat.id}.${attr}`.toLowerCase()
        });
      });
    });

    // Add node outputs
    (nodeOutputs || []).forEach(node => {
      (node.outputs || []).forEach(out => {
        list.push({
          label: `${node.nodeName}: ${out.name}`,
          token: `o.${out.id}`,
          type: "output",
          group: node.nodeName,
          icon: "🔗",
          searchText: `${node.nodeName} ${out.name} o.${out.id}`.toLowerCase()
        });
      });
    });

    // Add transform attributes
    (transformAttributes || []).forEach(nodeData => {
      (nodeData.transformations || []).forEach(t => {
        list.push({
          label: t.display_name,
          token: t.formula_token,
          type: "transform",
          group: nodeData.node_name,
          icon: "⚡",
          searchText: `${t.display_name} ${t.formula_token}`.toLowerCase()
        });
      });
    });

    // Add connected output token (previous node connection)
    if (connectedOutputAttributes && connectedOutputAttributes.id) {
      list.push({
        label: `Connected output (o.${connectedOutputAttributes.id})`,
        token: `o.${connectedOutputAttributes.id}`,
        type: "output",
        group: "Connected Output",
        icon: "🔗",
        searchText: `connected output o.${connectedOutputAttributes.id}`.toLowerCase()
      });
    }

    return list;
  }, [attributes, nodeOutputs, transformAttributes, connectedOutputAttributes]);

  // Get the current word being typed (for autocomplete)
  const getCurrentWord = useCallback((text, cursorPos) => {
    if (!text || cursorPos === 0) return "";

    // Find the start of the current word (looking for spaces or operators)
    let start = cursorPos - 1;
    while (start >= 0 && !/[ \t\n\r+\-*/()=]/.test(text[start])) {
      start--;
    }
    start++;

    // Find the end of the current word
    let end = cursorPos;
    while (end < text.length && !/[ \t\n\r+\-*/()=]/.test(text[end])) {
      end++;
    }

    return text.substring(start, end);
  }, []);

  // Handle input change with cursor tracking
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart || 0;

    setLocalValue(newValue);
    onChange(newValue);
    setCursorPosition(cursorPos);

    // Trigger validation
    debouncedValidate(newValue);

    // Get the current word being typed
    const currentWord = getCurrentWord(newValue, cursorPos);

    if (currentWord.length >= 1) {
      // Filter suggestions based on current word with safe fallbacks
      const filtered = allSuggestions.filter(s => {
        const searchText = (s.searchText || "").toLowerCase();
        const label = (s.label || "").toLowerCase();
        const token = (s.token || "").toLowerCase();
        const currentWordLower = currentWord.toLowerCase();

        return searchText.includes(currentWordLower) ||
          label.includes(currentWordLower) ||
          token.includes(currentWordLower);
      });
      setSuggestions(filtered.slice(0, 10));
      setShowSuggestions(filtered.length > 0);
      setAnchorEl(inputRef.current);
    } else {
      setSuggestions(allSuggestions.slice(0, 10));
      setShowSuggestions(allSuggestions.length > 0);
      setAnchorEl(inputRef.current);
    }
  };

  // Insert token at current cursor position
  const insertAtCursor = useCallback((insertText) => {
    const text = localValue;
    const cursorPos = cursorPosition;
    const before = text.substring(0, cursorPos);
    const after = text.substring(cursorPos);

    // Get the current word to replace
    let wordStart = cursorPos;
    while (wordStart > 0 && !/[ \t\n\r+\-*/()=]/.test(text[wordStart - 1])) {
      wordStart--;
    }

    let wordEnd = cursorPos;
    while (wordEnd < text.length && !/[ \t\n\r+\-*/()=]/.test(text[wordEnd])) {
      wordEnd++;
    }

    // If we're inserting a suggestion and there's a current word, replace it
    const isSuggestion = allSuggestions.some(s => s.token === insertText);
    if (isSuggestion && wordStart < wordEnd) {
      // Replace the current word with the token
      const newBefore = text.substring(0, wordStart);
      const newAfter = text.substring(wordEnd);
      const newValue = newBefore + insertText + newAfter;
      const newCursorPos = wordStart + insertText.length;

      setLocalValue(newValue);
      onChange(newValue);
      setCursorPosition(newCursorPos);

      // Trigger validation
      debouncedValidate(newValue);

      // Set cursor position after state update
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);
    } else {
      // Normal insertion
      const newValue = before + insertText + after;
      const newCursorPos = cursorPos + insertText.length;

      setLocalValue(newValue);
      onChange(newValue);
      setCursorPosition(newCursorPos);

      // Trigger validation
      debouncedValidate(newValue);

      // Set cursor position after state update
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);
    }

    setShowSuggestions(false);
  }, [localValue, cursorPosition, onChange, allSuggestions, debouncedValidate]);

  // Insert operator or number
  const insertOperator = useCallback((operator) => {
    insertAtCursor(operator);
  }, [insertAtCursor]);

  // Handle keydown for autocomplete selection
  const handleKeyDown = (e) => {
    if (e.key === "Tab" && suggestions.length > 0 && showSuggestions) {
      e.preventDefault();
      insertAtCursor(suggestions[0].token);
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  // Handle selection from dropdown
  const handleSuggestionClick = (token) => {
    insertAtCursor(token);
  };

  // Update cursor position on selection change
  const handleSelect = (e) => {
    setCursorPosition(e.target.selectionStart || 0);
  };

  // Clear validation timeout on unmount
  useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, []);

  // Re-validate when value changes from parent
  useEffect(() => {
    if (value !== localValue) {
      debouncedValidate(value);
    }
  }, [value, debouncedValidate, localValue]);

  // Also validate on initial load
  useEffect(() => {
    if (localValue) {
      debouncedValidate(localValue);
    }
  }, []); // Run once on mount

  return (
    <Box>
      {/* Main text input */}
      <TextField
        inputRef={inputRef}
        value={localValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onSelect={handleSelect}
        onClick={handleSelect}
        onFocus={() => {
          if (localValue.length === 0) {
            setSuggestions(allSuggestions.slice(0, 10));
            setShowSuggestions(allSuggestions.length > 0);
            setAnchorEl(inputRef.current);
          }
        }}
        disabled={disabled}
        placeholder={placeholder || "Build your formula (e.g., 1.quality + o.5 * 2)"}
        fullWidth
        multiline
        rows={2}
        error={!!formulaError}
        helperText={formulaError || validationWarning}
        FormHelperTextProps={{
          sx: {
            color: formulaError ? c.error : c.warning,
            fontSize: "11px",
            mt: 0.5,
          }
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "12px",
            background: disabled ? c.bg : c.surface,
            fontSize: "13px",
            fontFamily: "monospace",
            "& fieldset": { borderColor: c.border },
            "&:hover fieldset": { borderColor: "#94A3B8" },
            "&.Mui-focused fieldset": { borderColor: c.borderFocus, borderWidth: "2px" },
            "&.Mui-error fieldset": { borderColor: c.error },
          },
        }}
      />

      {/* Auto-suggestions dropdown */}
      <Popper
        open={showSuggestions && !disabled}
        anchorEl={anchorEl}
        placement="bottom-start"
        style={{ zIndex: 1400, width: anchorEl?.offsetWidth }}
        modifiers={[{ name: 'preventOverflow', options: { boundary: 'viewport' } }]}
      >
        <ClickAwayListener onClickAway={() => setShowSuggestions(false)}>
          <Paper
            elevation={8}
            sx={{
              borderRadius: "12px",
              border: `1px solid ${c.border}`,
              overflow: "hidden",
              mt: 1,
              maxHeight: 400,
              overflowY: "auto",
              boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)"
            }}
          >
            {suggestions.length === 0 ? (
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography sx={{ fontSize: "13px", color: c.textMuted }}>No matching items</Typography>
              </Box>
            ) : (
              <>
                {/* Group suggestions */}
                {["material", "output", "transform"].map(type => {
                  const grouped = suggestions.filter(s => s.type === type);
                  if (!grouped.length) return null;
                  const typeLabel = { material: "Materials", output: "Node Outputs", transform: "Transformations" }[type];
                  const style = TOKEN_STYLES[type] || { bg: "#F1F5F9", border: "#CBD5E1", color: "#475569" };
                  return (
                    <Box key={type} sx={{ borderBottom: `1px solid ${c.border}`, '&:last-child': { borderBottom: 'none' } }}>
                      <Typography sx={{
                        px: 2, py: 0.75, fontSize: "10px", fontWeight: 700, letterSpacing: "0.08em",
                        color: c.textMuted, textTransform: "uppercase", background: c.bg,
                        borderBottom: `1px solid ${c.border}`
                      }}>
                        {typeLabel}
                      </Typography>
                      {grouped.map((s, i) => (
                        <Tooltip
                          key={i}
                          title={`Token: ${s.token}`}
                          placement="right"
                          arrow
                        >
                          <Box
                            onClick={() => handleSuggestionClick(s.token)}
                            sx={{
                              px: 2, py: 1, cursor: "pointer", display: "flex", alignItems: "center", gap: 1.5,
                              "&:hover": { background: c.accentLight },
                              transition: "background 0.1s",
                            }}
                          >
                            <Typography sx={{ fontSize: "14px", flexShrink: 0 }}>{s.icon || "📦"}</Typography>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{ fontSize: "13px", color: c.text, fontWeight: 600 }}>
                                {s.label}
                              </Typography>
                              <Typography sx={{ fontSize: "11px", color: c.textSecondary, fontFamily: "monospace" }}>
                                {s.token}
                              </Typography>
                            </Box>
                          </Box>
                        </Tooltip>
                      ))}
                    </Box>
                  );
                })}
              </>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>

      {/* Quick-click operator chips */}
      {!disabled && (
        <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 0.75, alignItems: "center" }}>
          <Typography sx={{ fontSize: "11px", fontWeight: 600, color: c.textMuted, mr: 1 }}>
            Quick insert:
          </Typography>
          <Typography sx={{ fontSize: "10px", color: c.textMuted, mr: 0.5 }}>Operators:</Typography>
          {["+", "-", "*", "/", "(", ")", "="].map(op => (
            <Chip
              key={op}
              label={op}
              onClick={() => insertOperator(op)}
              size="small"
              sx={{
                borderRadius: "8px", background: "#FEF3C7", border: `1px solid #FDE68A`,
                color: "#92400E", fontSize: "13px", fontWeight: 700, cursor: "pointer",
                "&:hover": { background: c.accent, color: "#fff", borderColor: c.accent, transform: "translateY(-1px)" },
                transition: "all 0.2s",
              }}
            />
          ))}
          <Typography sx={{ fontSize: "10px", color: c.textMuted, mx: 1 }}>Numbers:</Typography>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 0, "."].map(n => (
            <Chip
              key={n}
              label={n}
              onClick={() => insertOperator(String(n))}
              size="small"
              sx={{
                borderRadius: "8px", background: c.chipBg, border: `1px solid ${c.chipBorder}`,
                color: c.textSecondary, fontSize: "12px", cursor: "pointer",
                "&:hover": { background: c.accent, color: "#fff", borderColor: c.accent, transform: "translateY(-1px)" },
                transition: "all 0.2s",
              }}
            />
          ))}
        </Box>
      )}

      {/* Live Preview Toggle - Only show if formula has no errors */}
      {!disabled && localValue?.trim() && !formulaError && (
        <Box sx={{ mt: 1.5, display: "flex", justifyContent: "flex-end" }}>
          <Button
            size="small"
            onClick={() => setShowLivePreview(!showLivePreview)}
            sx={{
              fontSize: "10px",
              fontWeight: 600,
              color: c.textSecondary,
              textTransform: "none",
              "&:hover": { color: c.accent },
            }}
            startIcon={<Iconify icon={showLivePreview ? "solar:eye-slash-bold" : "solar:eye-bold"} width={14} />}
          >
            {showLivePreview ? "Hide Live Preview" : "Show Live Preview"}
          </Button>
        </Box>
      )}

      {/* Live Calculation Preview - Only show if formula has no errors */}
      {showLivePreview && !disabled && localValue?.trim() && !formulaError && (
        <LiveCalculationPreview
          formulaStr={localValue}
          attributesList={attributes}
          nodeOutputs={nodeOutputs}
          transformAttributes={transformAttributes}
        />
      )}
    </Box>
  );
};

// ─── FormulaPreview: live evaluated formula display ───────────────────────────
const FormulaPreview = ({ formulaStr, attributes, nodeOutputs }) => {
  const preview = useMemo(() => {
    if (!formulaStr?.trim()) return null;
    // Build a human-readable version replacing tokens with names
    let readable = formulaStr;
    const allOutputs = nodeOutputs?.flatMap(n => n.outputs) || [];
    // Replace o.ID with output name
    readable = readable.replace(/o\.(\d+)/g, (match, id) => {
      const found = allOutputs.find(o => o.id === parseInt(id));
      return found ? `[${found.name}]` : match;
    });
    // Replace t.ID.ATTR with display
    readable = readable.replace(/t\.(\d+)\.(\w+)/g, (match, id, attr) => `[transform:${attr}]`);
    // Replace ID.ATTR with material name
    readable = readable.replace(/(\d+)\.(\w+)/g, (match, id, attr) => {
      const mat = attributes?.find(m => m.id === parseInt(id));
      return mat ? `[${mat.name}.${attr}]` : match;
    });
    return readable;
  }, [formulaStr, attributes, nodeOutputs]);

  if (!preview) return null;

  return (
    <Box sx={{ mt: 1.5, p: 2, borderRadius: "10px", background: "#F1F5F9", border: `1px dashed #CBD5E1`, display: "flex", alignItems: "flex-start", gap: 1.5 }}>
      <Typography sx={{ fontSize: "10px", fontWeight: 700, color: c.textMuted, textTransform: "uppercase", mt: 0.1, flexShrink: 0, letterSpacing: "0.06em" }}>Preview</Typography>
      <Typography sx={{ fontSize: "12px", color: c.text, fontFamily: "monospace", fontWeight: 500, lineHeight: 1.6, wordBreak: "break-all" }}>
        {preview}
      </Typography>
    </Box>
  );
};

const RightPanel = React.memo(({
  formulaValue,
  attributesList,
  nodeOutputs,
  transformAttributes,
  onTokenClick,
  nodeType,
  allMaterialAttributes,
  needsTransformation,
  selectedTransformAttributes,
  transformOperators,
  transformUserValues,
  onTransformationChange,
  onAttributeToggle,
  onOperatorChange,
  onValueChange,
  index,
  output,
  handleOutputChange,
  availableOutputs,
  isDisabled,
  finalProduct,
  finalProductAttributes,
  filteredNodeOutputs,
  setOutputs,
  outputs,
  affectedoutput,
  focusedOptionId,
  connectedOutputAttributes,
  onInsertOperator, // New prop for inserting operators
  onInsertFunction,
  hasOptions,
  optionConfig
}) => {
  // Use a ref to preserve scroll position
  const scrollContainerRef = useRef(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [activeTab, setActiveTab] = useState(0); // 0: Attributes, 1: Functions

  // Save scroll position before updates
  const saveScrollPosition = useCallback(() => {
    if (scrollContainerRef.current) {
      setScrollPosition(scrollContainerRef.current.scrollTop);
    }
  }, []);

  // Restore scroll position after updates
  useEffect(() => {
    if (scrollContainerRef.current && scrollPosition > 0) {
      scrollContainerRef.current.scrollTop = scrollPosition;
    }
  }, [scrollPosition]);

  const handleAttributeClick = useCallback((token) => {
    if (onTokenClick) {
      saveScrollPosition();
      onTokenClick(token);
    }
  }, [onTokenClick, saveScrollPosition]);

  const handleOperatorInsert = useCallback((operator) => {
    if (onInsertOperator) {
      saveScrollPosition();
      onInsertOperator(operator);
    }
  }, [onInsertOperator, saveScrollPosition]);

  const handleFunctionInsert = useCallback((func) => {
    if (onInsertFunction) {
      saveScrollPosition();
      onInsertFunction(func);
    }
  }, [onInsertFunction, saveScrollPosition]);

  // Handle radio button changes without losing scroll
  const handleRadioChange = useCallback((e, field) => {
    saveScrollPosition();
    handleOutputChange(index, field, e.target.value);
  }, [handleOutputChange, index, saveScrollPosition]);

  // Handle transformation changes without losing scroll
  const handleTransformationRadioChange = useCallback((value) => {
    saveScrollPosition();
    onTransformationChange(index, value);
  }, [onTransformationChange, index, saveScrollPosition]);

  const handleAttributeToggleWrapper = useCallback((productId, attribute) => {
    saveScrollPosition();
    onAttributeToggle(index, productId, attribute);
  }, [onAttributeToggle, index, saveScrollPosition]);

  const handleOperatorChangeWrapper = useCallback((key, operator) => {
    saveScrollPosition();
    onOperatorChange(index, key, operator);
  }, [onOperatorChange, index, saveScrollPosition]);

  const handleValueChangeWrapper = useCallback((key, value) => {
    saveScrollPosition();
    onValueChange(index, key, value);
  }, [onValueChange, index, saveScrollPosition]);

  // Compute list of selected transformed attributes for formula use
  const selectedTransformedAttributesForFormula = useMemo(() => {
    const selected = [];
    
    // Get the instance key (handle both option-based and simple cases)
    const instanceKey = optionConfig?.options?.length > 0 && focusedOptionId 
      ? `${index}_${focusedOptionId}` 
      : `${index}`;
    
    // Get selected transform attributes for current instance
    const selectedKeys = selectedTransformAttributes?.[instanceKey] || [];
    
    if (selectedKeys.length === 0) return selected;

    // Build list of selected transformed attributes with their formula tokens
    selectedKeys.forEach(key => {
      const [productId, attribute] = key.split(":");
      const info = allMaterialAttributes?.find(
        a => a.productId === Number(productId) && a.attribute === attribute
      );
      
      if (info) {
        // Create formula token for transformed attribute (t.outputId.attribute)
        // We use output_id from transformOperators or a generated identifier
        const operator = transformOperators?.[instanceKey]?.[key] || "+";
        const value = transformUserValues?.[instanceKey]?.[key];
        
        // Generate a unique identifier for this transformation
        const transformId = `t_${instanceKey}_${key.replace(":", "_")}`;
        
        selected.push({
          id: transformId,
          displayName: `${info.displayName} (${operator}${value || "?"})`,
          formulaToken: transformId,
          type: "selected-transform",
          originalKey: key,
          operator,
          value
        });
      }
    });
    
    return selected;
  }, [selectedTransformAttributes, transformOperators, transformUserValues, allMaterialAttributes, index, optionConfig, focusedOptionId]);

  // Operators for quick input
  const operators = ["+", "-", "*", "/", "(", ")", "="];

  return (
    <Box sx={{
      display: "flex",
      flexDirection: "column",
      gap: 3,
      height: "100%",
      overflow: "hidden",
      position: "relative",
    }}>
      {/* Tabs */}
      <Box sx={{
        display: "flex",
        gap: 1,
        borderBottom: `1.5px solid ${c.border}`,
        px: 1,
      }}>
        <Button
          size="small"
          onClick={() => setActiveTab(0)}
          sx={{
            fontSize: "12px",
            fontWeight: 600,
            color: activeTab === 0 ? c.accent : c.textSecondary,
            textTransform: "none",
            borderBottom: activeTab === 0 ? `2px solid ${c.accent}` : "none",
            borderRadius: 0,
            pb: 1,
            "&:hover": {
              background: "transparent",
              color: c.accent,
            }
          }}
        >
          Attributes
        </Button>

      </Box>

      <Box
        ref={scrollContainerRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          pr: 1,
          display: "flex",
          flexDirection: "column",
          gap: 3,
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: c.border,
            borderRadius: "3px",
            "&:hover": {
              background: c.textMuted,
            }
          },
        }}
      >




        <Box sx={{
          background: c.surface,
          border: `1.5px solid ${c.border}`,
          borderRadius: "20px",
          overflow: "hidden",
          flexShrink: 0,
          boxShadow: "0 8px 16px -6px rgba(0,0,0,0.08), 0 2px 4px -2px rgba(0,0,0,0.02)",
        }}>
          <Box sx={{
            px: 2.5,
            py: 2,
            borderBottom: `1px solid ${c.border}`,
            background: c.bg,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{
                width: 8,
                height: 8,
                borderRadius: "2px",
                background: `linear-gradient(135deg, ${c.accent}, ${c.accentHover})`,
              }} />
              <Typography sx={{
                fontSize: "12px",
                fontWeight: 800,
                letterSpacing: "0.1em",
                color: c.textSecondary,
                textTransform: "uppercase",
              }}>
                Available Attributes
              </Typography>
            </Box>
          </Box>

          <Box sx={{
            p: 2.5,
            maxHeight: 400,
            overflowY: "auto",
          }}>
            {attributesList.length === 0 && filteredNodeOutputs.length === 0 && transformAttributes.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography sx={{ fontSize: "12px", color: c.textMuted }}>No attributes available</Typography>
              </Box>
            ) : (
              <>
                {/* Material attributes */}
                {attributesList.map(mat => (
                  <Box key={mat.id} sx={{ mb: 2.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "2px", background: `linear-gradient(135deg, ${c.accent}, ${c.accentHover})` }} />
                      <Typography sx={{ fontSize: "10px", fontWeight: 800, color: c.textSecondary }}>
                        {mat.name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                      {mat.attributes.map(attr => (
                        <Chip
                          key={attr}
                          label={attr}
                          size="small"
                          // onClick={() => handleAttributeClick(`${mat.id}.${attr}`)}
                          sx={{


                            fontSize: "11px",
                            cursor: "pointer",
                            height: 26,

                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                ))}

                {/* Final product */}
                {finalProduct && finalProductAttributes.length > 0 && (
                  <Box sx={{ mb: 2.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "2px", background: `linear-gradient(135deg, ${c.purple}, #A855F7)` }} />
                      <Typography sx={{ fontSize: "10px", fontWeight: 800, color: c.purple }}>
                        {finalProduct.name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                      {finalProductAttributes.map(attr => (
                        <Chip
                          key={attr}
                          label={attr}
                          size="small"
                          onClick={() => handleAttributeClick(`${finalProduct.id}.${attr}`)}
                          sx={{ borderRadius: "8px", fontSize: "11px", cursor: "pointer", height: 26 }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Node outputs */}
                {filteredNodeOutputs.map(node => (
                  <Box key={node.nodeId} sx={{ mb: 2.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "2px", background: `linear-gradient(135deg, ${c.blue}, #3B82F6)` }} />
                      <Typography sx={{ fontSize: "10px", fontWeight: 800, color: c.blue }}>
                        {node.nodeName}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                      {node.outputs.map(out => (
                        <Chip
                          key={out.id}
                          label={out.name}
                          size="small"
                          onClick={() => handleAttributeClick(`o.${out.id}`)}
                          sx={{ borderRadius: "8px", fontSize: "11px", cursor: "pointer", height: 26 }}
                        />
                      ))}
                    </Box>
                  </Box>
                ))}

                {/* Connected output attributes (previous node) */}
                {output.isConnected === "yes" && output.connectedOutput && connectedOutputAttributes?.[index]?.attributes?.length > 0 && (
                  <Box sx={{ mb: 2.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "2px", background: "linear-gradient(135deg, #F59E0B, #D97706)" }} />
                      <Typography sx={{ fontSize: "10px", fontWeight: 800, color: "#D97706" }}>
                        Connected Output: {output.connectedOutput}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                      {connectedOutputAttributes[index].attributes.map(attr => (
                        <Chip
                          key={`${connectedOutputAttributes[index].id}-${attr}`}
                          label={`o.${connectedOutputAttributes[index].id}`}
                          size="small"
                          onClick={() => handleAttributeClick(`o.${connectedOutputAttributes[index].id}`)}
                          sx={{ borderRadius: "8px", fontSize: "11px", cursor: "pointer", height: 26 }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Transform attributes */}
                {transformAttributes.map(nodeData => (
                  <Box key={nodeData.node_id} sx={{ mb: 2.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "2px", background: `linear-gradient(135deg, #C2410C, #F97316)` }} />
                      <Typography sx={{ fontSize: "10px", fontWeight: 800, color: "#C2410C" }}>
                        {nodeData.node_name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                      {nodeData.transformations.map(t => (
                        <Chip
                          key={`${t.output_id}-${t.attribute}`}
                          label={t.display_name}
                          size="small"
                          onClick={() => handleAttributeClick(t.formula_token)}
                          sx={{ borderRadius: "8px", fontSize: "11px", cursor: "pointer", height: 26 }}
                        />
                      ))}
                    </Box>
                  </Box>
                ))}

                {/* Selected Transformed Attributes - Only show those user selected in transformation section */}
                {selectedTransformedAttributesForFormula.length > 0 && (
                  <Box sx={{ mb: 2.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "2px", background: `linear-gradient(135deg, #10B981, #059669)` }} />
                      <Typography sx={{ fontSize: "10px", fontWeight: 800, color: "#059669" }}>
                        Selected Transformations
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                      {selectedTransformedAttributesForFormula.map(attr => (
                        <Chip
                          key={attr.id}
                          label={attr.displayName}
                          size="small"
                          onClick={() => handleAttributeClick(attr.formulaToken)}
                          sx={{
                            borderRadius: "8px",
                            fontSize: "11px",
                            cursor: "pointer",
                            height: 26,
                            background: "#ECFDF5",
                            border: `1.5px solid #10B981`,
                            color: "#059669",
                            fontWeight: 600,
                            "&:hover": {
                              background: "#D1FAE5",
                              transform: "translateY(-1px)",
                            },
                            transition: "all 0.2s",
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
        {/* Transformation Section */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {hasOptions && optionConfig?.options?.length > 0 ? (
            // Render separate transformation section for each option
            optionConfig.options.map(option => (
              <TransformationSection
                key={`${index}_${option.id}`}
                nodeType={nodeType}
                allMaterialAttributes={allMaterialAttributes}
                needsTransformation={needsTransformation}
                selectedTransformAttributes={selectedTransformAttributes}
                transformOperators={transformOperators}
                transformUserValues={transformUserValues}
                onTransformationChange={onTransformationChange}
                onAttributeToggle={onAttributeToggle}
                onOperatorChange={onOperatorChange}
                onValueChange={onValueChange}
                index={index}
                optionId={option.id}
                optionLabel={option.label}
              />
            ))
          ) : (
            // Single transformation section for simple case
            <TransformationSection
              nodeType={nodeType}
              allMaterialAttributes={allMaterialAttributes}
              needsTransformation={needsTransformation}
              selectedTransformAttributes={selectedTransformAttributes}
              transformOperators={transformOperators}
              transformUserValues={transformUserValues}
              onTransformationChange={onTransformationChange}
              onAttributeToggle={onAttributeToggle}
              onOperatorChange={onOperatorChange}
              onValueChange={onValueChange}
              index={index}
            />
          )}
        </Box>

        {/* Connection Settings */}
        {nodeType !== "diamond" && (
          <Box sx={{
            background: c.surface,
            border: `1.5px solid ${c.border}`,
            borderRadius: "20px",
            overflow: "hidden",
            flexShrink: 0,
          }}>
            <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${c.border}`, background: c.bg }}>
              <Typography sx={{ fontSize: "12px", fontWeight: 800, color: c.textSecondary }}>
                Connection Settings
              </Typography>
            </Box>
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 2.5 }}>
              <Box>
                <Typography sx={{ fontSize: "11px", fontWeight: 700, color: c.textSecondary, mb: 1 }}>
                  🔗 Connected to another output?
                </Typography>
                <RadioGroup row value={output.isConnected} onChange={(e) => handleRadioChange(e, "isConnected")}>
                  <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
                </RadioGroup>
                {output.isConnected === "yes" && (
                  <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
                    <InputLabel>Connected Output</InputLabel>
                    <Select
                      value={output.connectedOutput}
                      onChange={(e) => handleOutputChange(index, "connectedOutput", e.target.value)}
                      label="Connected Output"
                      disabled={isDisabled}
                    >
                      {availableOutputs.map(out => <MenuItem key={out.name} value={out.name}>{out.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                )}
              </Box>
              <Box>
                <Typography sx={{ fontSize: "11px", fontWeight: 700, color: c.textSecondary, mb: 1 }}>
                  ⚡ Input for next process?
                </Typography>
                <RadioGroup row value={output.inputForNextProcess} onChange={(e) => handleRadioChange(e, "inputForNextProcess")}>
                  <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
                </RadioGroup>
              </Box>
            </Box>
          </Box>
        )}


      </Box>
    </Box>
  );
});

RightPanel.displayName = 'RightPanel';

// ─── SectionHeader ────────────────────────────────────────────────────────────
const SectionHeader = ({ label, icon, badge }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
    <Box sx={{ width: 4, height: 18, borderRadius: 2, background: c.borderFocus }} />
    <Typography sx={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.08em", color: c.textSecondary, textTransform: "uppercase" }}>{label}</Typography>
    {badge !== undefined && (
      <Box sx={{ ml: "auto", px: 1.25, py: 0.25, borderRadius: "12px", background: c.accentLight, border: `1px solid ${c.border}` }}>
        <Typography sx={{ fontSize: "10px", fontWeight: 700, color: c.textSecondary }}>{badge}</Typography>
      </Box>
    )}
  </Box>
);

// ─── AddUnitDialog ─────────────────────────────────────────────────────────────
const AddUnitDialog = ({ open, onClose, onRefresh, onSave, existingUnits }) => {
  const [unitAbbreviation, setUnitAbbreviation] = useState("");
  const handleSave = async () => {
    if (existingUnits.includes(unitAbbreviation.trim())) { alert("This unit already exists"); return; }
    await postFetcher(["/new/workflow-app/measurement/unit/create/", { name: unitAbbreviation.trim() }]);
    if (onRefresh) onRefresh();
    onSave(unitAbbreviation.trim());
    setUnitAbbreviation("");
    onClose();
  };
  return (
    <Dialog open={open} onClose={() => { setUnitAbbreviation(""); onClose(); }} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px" } }}>
      <DialogTitle sx={{ fontSize: "15px", fontWeight: 700, color: c.text, borderBottom: `1px solid ${c.border}`, pb: 1.5 }}>Add New Unit</DialogTitle>
      <DialogContent sx={{ pt: 2.5 }}>
        <TextField label="Unit Abbreviation" value={unitAbbreviation} onChange={e => setUnitAbbreviation(e.target.value)} fullWidth margin="normal" placeholder="e.g., kg" sx={fieldSx} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={() => { setUnitAbbreviation(""); onClose(); }} variant="outlined" sx={{ borderRadius: "8px", borderColor: c.border, color: c.textSecondary, fontSize: "13px" }}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={!unitAbbreviation.trim()} sx={{ borderRadius: "8px", background: c.accent, color: "#fff", fontSize: "13px", boxShadow: "none", "&:hover": { background: c.accentHover } }}>Add Unit</Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main AddOutputDialog ──────────────────────────────────────────────────────
const AddOutputDialog = ({
  open, onClose, onSave, rawMaterialInputs, editRawMaterialInputs,
  previousNodeInputs, showOutputDialog, allocatedoutputs = [], finalproduct,
  nodeType, editingOutput, setupdatedoutputids, allOutputIds, setAllOutputIds,
  engineRef, setAllocatedoutputs, handleNodeSelection, editPreviousNodeInputs,
  setNodes, setAvailableNodes, onUnitsSelected, outputids, predefinedoutput,
  predefined, setPredefined, directLinks = [], downstreamLinks = [],
  directNodes = [], downstreamNodes = [], setDirectLinks, setDownstreamLinks,
  setDirectNodes, setDownstreamNodes, selectedLinks = {}, setSelectedLinks,
  selectedNodes = {}, setSelectedNodes, showdeleteeditDialog, setdeleteeditDialog,
  onDeleteOutput, rfNodes = [], setRfNodes, rfEdges = [], setRfEdges, onRefresh,
  nodes, affectedoutput, optionConfig, setOutputPayload
}) => {
  const [numberOfOutputs, setNumberOfOutputs] = useState(1);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [currentTab, setCurrentTab] = useState(0);
  const [outputs, setOutputs] = useState([{ name: "", outputName: "", unit: "", description: "", quantity: 0, isConnected: "no", connectedOutput: "", inputForNextProcess: "no", question: "", measurement_unit: "" }]);
  const [availableUnits, setAvailableUnits] = useState([]);
  const [showAddUnitDialog, setShowAddUnitDialog] = useState(false);
  const [attributesList, setAttributesList] = useState([]);
  const [transformAttributes, setTransformAttributes] = useState([]);
  const [loadingTransformAttributes, setLoadingTransformAttributes] = useState(false);
  const [nodeOutputs, setNodeOutputs] = useState([]);
  const [selectedNodeOutput, setSelectedNodeOutput] = useState({});
  const [availableOutputs, setAvailableOutputs] = useState([]);
  const [lastPostedIndex, setLastPostedIndex] = useState(-1);
  const [permanentdeleteeditnode, setpermanentdeleteeditnode] = useState(false);
  const [savedOutputIds, setSavedOutputIds] = useState(() => { const s = sessionStorage.getItem("savedOutputIds"); return s ? JSON.parse(s) : []; });
  const [connectedOutputAttributes, setConnectedOutputAttributes] = useState({});
  const [linked_attributes, setLinkedAttributes] = useState([]);
  const [question, setquestion] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [outputToDelete, setOutputToDelete] = useState(null);
  const [availableMeasurementUnits, setAvailableMeasurementUnits] = useState([]);
  const [focusedOptionId, setFocusedOptionId] = useState(null);

  // ── Transformation states ──────────────────────────────────────────────────
  const [needsTransformation, setNeedsTransformation] = useState({});
  const [selectedTransformAttributes, setSelectedTransformAttributes] = useState({});
  const [transformOperators, setTransformOperators] = useState({});
  const [transformUserValues, setTransformUserValues] = useState({});

  const stableRawMaterialInputs = useMemo(() => rawMaterialInputs || [], [rawMaterialInputs]);
  const stableNodeInputs = useMemo(() => previousNodeInputs.length > 0 ? previousNodeInputs : editPreviousNodeInputs || [], [previousNodeInputs, editPreviousNodeInputs]);
console.log('currenttab',currentTab)
  // ── All original handlers preserved ──────────────────────────────────────
  const handleLocalDelete = (index) => {
    const deletedOutput = outputs[index];
    const newOutputs = outputs.filter((_, i) => i !== index);
    setOutputs(newOutputs);
    setNumberOfOutputs(newOutputs.length);
    setLinkedAttributes(prev => prev.filter((_, i) => i !== index));
    if (deletedOutput?.id) setAllOutputIds(prev => prev.filter(id => id !== deletedOutput.id));
    enqueueSnackbar(`Output ${index + 1} removed locally`, { variant: "info" });
  };

  const handleClosedelete = () => {
    try {
      if (setDirectLinks) setDirectLinks([]);
      if (setDownstreamLinks) setDownstreamLinks([]);
      if (setDirectNodes) setDirectNodes([]);
      if (setDownstreamNodes) setDownstreamNodes([]);
      if (setSelectedLinks) setSelectedLinks({});
      if (setSelectedNodes) setSelectedNodes({});
      setDeleteConfirmOpen(false);
      setOutputToDelete(null);
    } catch (error) { console.error("Error in handleClosedelete:", error); }
  };

  const handleDeleteOutput = (index) => {
    const output = outputs[index];
    if (!output?.id) { handleLocalDelete(index); return; }
    if (onDeleteOutput) onDeleteOutput(output.id);
    setOutputToDelete({ index, outputId: output.id });
    setDeleteConfirmOpen(true);
  };

  const fetchTransformAttributes = async () => {
    try {
      setLoadingTransformAttributes(true);
      const nodeIds = nodes.map(node => node.id).filter(id => id != null);
      if (nodeIds.length === 0) { setTransformAttributes([]); return; }
      const response = await postFetcher(["/new/workflow-app/nodes/transformed-attributes/", { node_ids: nodeIds }]);
      if (response) setTransformAttributes(response);
    } catch (error) {
      console.error("Error fetching transform attributes:", error);
    } finally {
      setLoadingTransformAttributes(false);
    }
  };

  const confirmDelete = async () => {
    const { index, outputId } = outputToDelete;
    try {
      const selectedLinkIds = Object.keys(selectedLinks).filter(id => selectedLinks[id]);
      const selectedNodeIds = Object.keys(selectedNodes).filter(id => selectedNodes[id]);
      const allLinks = [...directLinks, ...downstreamLinks];
      const incomingLinksToDeletedNodes = allLinks.filter(link => selectedNodeIds.includes(link.target));
      const sourceNodeOutputIds = [];
      incomingLinksToDeletedNodes.forEach(link => { if (link.outputId) sourceNodeOutputIds.push(link.outputId); });
      if (sourceNodeOutputIds.length > 0) setAllocatedoutputs(prev => prev.filter(id => !sourceNodeOutputIds.includes(id)));

      if (selectedLinkIds.length > 0 && selectedNodeIds.length === 0) {
        if (rfEdges && setRfEdges) setRfEdges(prev => prev.filter(edge => !selectedLinkIds.includes(edge.id)));
        selectedLinkIds.forEach(linkId => {
          const link = allLinks.find(l => l.id === linkId);
          if (link?.outputId && setAllocatedoutputs) setAllocatedoutputs(prev => prev.filter(id => id !== link.outputId));
        });
        const remainingLinks = allLinks.filter(link => !selectedLinkIds.includes(link.id));
        if (setDirectLinks) setDirectLinks(remainingLinks.filter(link => directLinks.some(dl => dl.id === link.id)));
        if (setDownstreamLinks) setDownstreamLinks(remainingLinks.filter(link => downstreamLinks.some(dl => dl.id === link.id)));
        const remainingSelectedLinks = {};
        Object.keys(selectedLinks).forEach(linkId => { if (!selectedLinkIds.includes(linkId)) remainingSelectedLinks[linkId] = selectedLinks[linkId]; });
        setSelectedLinks(remainingSelectedLinks);
        handleLocalDelete(index);
        enqueueSnackbar("Edge deleted successfully", { variant: "success" });
        setdeleteeditDialog(false); setDeleteConfirmOpen(false); setOutputToDelete(null);
        return;
      }

      if (selectedNodeIds.length > 0) {
        if (rfEdges && setRfEdges) setRfEdges(prev => prev.filter(edge => !selectedNodeIds.includes(edge.source) && !selectedNodeIds.includes(edge.target)));
        if (rfNodes && setRfNodes) setRfNodes(prev => prev.filter(node => !selectedNodeIds.includes(node.id)));
        const collectedOutputIds = [];
        const remainingLinks = allLinks.filter(link => !selectedLinkIds.includes(link.id));
        if (setDirectLinks) setDirectLinks(remainingLinks.filter(link => directLinks.some(dl => dl.id === link.id)));
        if (setDownstreamLinks) setDownstreamLinks(remainingLinks.filter(link => downstreamLinks.some(dl => dl.id === link.id)));
        const remainingSelectedLinks = {};
        Object.keys(selectedLinks).forEach(linkId => { if (!selectedLinkIds.includes(linkId)) remainingSelectedLinks[linkId] = selectedLinks[linkId]; });
        setSelectedLinks(remainingSelectedLinks);
        selectedLinkIds.forEach(linkId => {
          const link = allLinks.find(l => l.id === linkId);
          if (link?.outputId) { collectedOutputIds.push(link.outputId); if (setAllocatedoutputs) setAllocatedoutputs(prev => prev.filter(id => id !== link.outputId)); }
        });
        const allNodes = [...directNodes, ...downstreamNodes];
        const remainingNodes = allNodes.filter(node => !selectedNodeIds.includes(node.id));
        if (setDirectNodes) setDirectNodes(remainingNodes.filter(node => directNodes.some(dn => dn.id === node.id)));
        if (setDownstreamNodes) setDownstreamNodes(remainingNodes.filter(node => downstreamNodes.some(dn => dn.id === node.id)));
        const remainingSelectedNodes = {};
        Object.keys(selectedNodes).forEach(nodeId => { if (!selectedNodeIds.includes(nodeId)) remainingSelectedNodes[nodeId] = selectedNodes[nodeId]; });
        setSelectedNodes(remainingSelectedNodes);
        const backendIdsToDelete = [];
        const nodeNamesToDelete = [];
        selectedNodeIds.forEach(nodeId => { const node = allNodes.find(n => n.id === nodeId); if (node?.backendId) { backendIdsToDelete.push(node.backendId); nodeNamesToDelete.push(node.name); } });
        if (collectedOutputIds.length > 0) { try { await postFetcher([`/new/workflow-app/delete/outputs/`, { ids: collectedOutputIds }]); } catch (e) { console.error("Error deleting outputs:", e); } }
        if (backendIdsToDelete.length > 0) {
          try {
            if (setAvailableNodes) {
              setAvailableNodes(prev => prev.map(node => {
                if (nodeNamesToDelete.includes(node.name) || backendIdsToDelete.includes(node.backendId)) {
                  return { backendId: node.backendId, id: node.id, name: node.name, type: node.type || "default", color: node.color || "#00c0ff", isPredefined: node.isPredefined || false, input: [], output: [], cost: [], machine: [], attachments: [], input_foreign: [], output_foreign: [], cost_foreign: [], machine_data: [], input_foreign_ids: [], output_foreign_ids: [], cost_foreign_ids: [] };
                }
                return node;
              }));
            }
            if (setNodes) setNodes(prev => prev.filter(node => !backendIdsToDelete.includes(node.id)));
          } catch (e) { enqueueSnackbar("Failed to delete nodes from backend", { variant: "error" }); }
        }
      }
      handleLocalDelete(index);
      enqueueSnackbar("Output deleted successfully", { variant: "success" });
      setdeleteeditDialog(false);
    } catch (error) {
      enqueueSnackbar("Failed to delete output and dependencies", { variant: "error" });
    } finally {
      setDeleteConfirmOpen(false); setOutputToDelete(null);
    }
  };

  useEffect(() => { sessionStorage.setItem("savedOutputIds", JSON.stringify(savedOutputIds)); }, [savedOutputIds]);

  const getRawFormulaString = (formulaObj) => {
    if (!formulaObj) return "";
    if (formulaObj.formulaStr && typeof formulaObj.formulaStr === "string") return formulaObj.formulaStr.trim();
    if (!formulaObj.operation || !Array.isArray(formulaObj.operands)) return "";
    const opMap = { add: "+", subtract: "-", multiply: "*", divide: "/" };
    const operator = opMap[formulaObj.operation] || formulaObj.operation;
    const processOperand = (op) => { if (typeof op === "number") return op.toString(); if (typeof op === "string") return op; if (op && typeof op === "object") return `(${getRawFormulaString(op)})`; return ""; };
    return formulaObj.operands.map(processOperand).filter(Boolean).join(` ${operator} `);
  };

  useEffect(() => {
    if (!open) return;
    if (editingOutput) {
      if (Array.isArray(editingOutput)) {
        const loadedOutputs = editingOutput.map(output => ({ id: output.id, outputName: output.name || "", name: getRawFormulaString(output.formula) || "", unit: output.unit || "", description: output.description || "", quantity: output.value?.quantity || 0, isConnected: output.parent_output ? "yes" : "no", connectedOutput: "", inputForNextProcess: output.input_for_next_process ? "yes" : "no", question: output.question || "" }));
        setNumberOfOutputs(loadedOutputs.length); setOutputs(loadedOutputs); setAllOutputIds(editingOutput.map(o => o.id)); setquestion(editingOutput[0].question);
      } else {
        setAllOutputIds([editingOutput.id]);
        setOutputs([{ id: editingOutput.id, outputName: editingOutput.name || "", name: getRawFormulaString(editingOutput.formula), unit: editingOutput.unit || "", description: editingOutput.description || "", quantity: editingOutput.value?.quantity || 0, isConnected: editingOutput.parent_output ? "yes" : "no", connectedOutput: "", inputForNextProcess: editingOutput.input_for_next_process ? "yes" : "no", question: editingOutput.question || "" }]);
      }
    } else {
      setAllOutputIds([]); setNumberOfOutputs(1);
      setOutputs([{ name: "", outputName: "", unit: "", description: "", quantity: 0, isConnected: "no", connectedOutput: "", inputForNextProcess: "no", question: "" }]);
      setLinkedAttributes([]);
    }
  }, [open, editingOutput]);

  const getAllAvailableLinkedAttributes = useMemo(() => {
    const allAttributes = [];
    attributesList.forEach(material => { material.attributes.forEach(attribute => { allAttributes.push({ productId: material.id, attribute, displayName: `${material.name}: ${attribute}`, type: "material" }); }); });
    nodeOutputs.forEach(node => { node.outputs.forEach(output => { if (output.linked_attributes?.length > 0) { output.linked_attributes.forEach(attr => { const product = attributesList.find(m => m.id === attr.productId); const productName = product ? product.name : `Product ${attr.productId}`; allAttributes.push({ productId: attr.productId, attribute: attr.attribute, displayName: `${output.name} (${node.nodeName}): ${productName}.${attr.attribute}`, type: "node_output", nodeName: node.nodeName, outputName: output.name }); }); } }); });
    return allAttributes;
  }, [attributesList, nodeOutputs]);

  useEffect(() => {
    if (open && getAllAvailableLinkedAttributes.length > 0) {
      const newLinkedAttributes = [];
      for (let i = 0; i < numberOfOutputs; i++) { if (i < linked_attributes.length) newLinkedAttributes[i] = linked_attributes[i]; else newLinkedAttributes[i] = getAllAvailableLinkedAttributes.map(attr => ({ productId: attr.productId, attribute: attr.attribute })); }
      setLinkedAttributes(newLinkedAttributes);
    }
  }, [numberOfOutputs, open, getAllAvailableLinkedAttributes]);

  useEffect(() => {
    if (open && getAllAvailableLinkedAttributes.length > 0) {
      setLinkedAttributes(outputs.map(() => getAllAvailableLinkedAttributes.map(attr => ({ productId: attr.productId, attribute: attr.attribute }))));
    }
  }, [open, getAllAvailableLinkedAttributes, outputs.length]);

  useEffect(() => { if (open) { setAvailableOutputs([]); setSavedOutputIds([]); setSelectedNodeOutput({}); setLinkedAttributes([]); } }, [open]);

  const filteredNodeOutputs = useMemo(() => {
    if (!nodeOutputs.length) return [];
    const isEditing = !!editPreviousNodeInputs;
    return nodeOutputs.map(node => ({ ...node, outputs: node.outputs.filter(output => isEditing ? true : !allocatedoutputs.includes(output.id.toString())) })).filter(node => node.outputs.length > 0);
  }, [nodeOutputs, allocatedoutputs, editPreviousNodeInputs]);

  useEffect(() => { if (!showOutputDialog) { setAvailableOutputs([]); setSavedOutputIds([]); setSelectedNodeOutput({}); setLinkedAttributes([]); } }, [showOutputDialog]);

  const fetchNodeAttributes = async () => {
    try {
      if (!stableNodeInputs?.length) { setNodeOutputs([]); return; }
      const nodeOutputsData = [];
      for (const node of stableNodeInputs) {
        if (node.backendId) {
          const res = await fetcher(`/new/workflow-app/node/${node.backendId}/`);
          if (res?.output_foreign) {
            const outputs = res.output_foreign.map(output => ({ id: output.id, name: output.name, attributes: output.attributes.map(attr => attr.key), linked_attributes: output.linked_attributes || [], nodeName: node.label }));
            nodeOutputsData.push({ nodeId: node.backendId, nodeName: node.label, outputs });
          }
        }
      }
      setNodeOutputs(nodeOutputsData.length ? nodeOutputsData : []);
    } catch (error) { setNodeOutputs([]); enqueueSnackbar("Failed to fetch node outputs", { variant: "error" }); }
  };

  const fetchUnits = async () => {
    try {
      const rawMaterialIds = stableRawMaterialInputs.map(m => Number(m.value));
      const finalProductIds = finalproduct && Array.isArray(finalproduct) ? finalproduct.map(p => Number(p.id)) : [];
      const productIds = [...rawMaterialIds, ...finalProductIds];
      const res = await postFetcher(["/new/workflow-app/products/unit/", { product_ids: productIds || [] }]);
      let allUnits = [];
      if (res) {
        if (Array.isArray(res.category_data)) res.category_data.forEach(cat => { if (Array.isArray(cat.units)) allUnits.push(...cat.units); });
        if (Array.isArray(res.units_data)) res.units_data.forEach(item => { if (item?.name) allUnits.push(item.name); });
        setAvailableUnits([...new Set(allUnits.filter(u => u?.trim()))]);
      } else setAvailableUnits([]);
    } catch (error) { setAvailableUnits([]); enqueueSnackbar("Failed to fetch units", { variant: "error" }); }
  };

  const fetchAttributes = async () => {
    try {
      const rawMaterialIds = stableRawMaterialInputs.map(m => Number(m.value));
      const finalProductIds = finalproduct && Array.isArray(finalproduct) ? finalproduct.map(p => Number(p.id)) : [];
      const productIds = [...rawMaterialIds, ...finalProductIds];
      if (!productIds.length) { setAttributesList([]); return; }
      const res = await postFetcher(["/new/workflow-app/products/attributes/key/", { product_ids: productIds }]);
      if (res) {
        const attributes = Array.isArray(res) ? res.map(item => ({ name: item.name, attributes: item.attributes || [], id: item.id })) : res.data?.map(item => ({ name: item.name, attributes: item.attributes || [], id: item.id })) || [];
        setAttributesList(attributes.length ? attributes : []);
      } else setAttributesList([]);
    } catch (error) { setAttributesList([]); enqueueSnackbar("Failed to fetch attributes", { variant: "error" }); }
  };

  const fetchAvailableOutputs = async () => {
    try {
      if (savedOutputIds.length > 0) {
        const res = await postFetcher(["/new/workflow-app/output/list/", { output_ids: savedOutputIds }]);
        if (res) {
          const outs = Array.isArray(res) ? res : res.data?.outputs || [];
          const mappedOutputs = outs.map((output, index) => {
            const operands = output.formula?.operands || [];
            const attributes = operands.filter(op => typeof op === "string" && op.includes(".")).map(op => op.split(".")[1]) || [];
            return { name: output.output_name, index: output.output_id || index + 1, attributes };
          }).filter(o => o.name?.trim());
          setAvailableOutputs(mappedOutputs.length ? mappedOutputs : []);
        } else setAvailableOutputs([]);
      }
    } catch (error) { setAvailableOutputs([]); enqueueSnackbar("Failed to fetch outputs", { variant: "error" }); }
  };

  const fetchConnectedOutputAttributes = async (outputName, index) => {
    try {
      const output = availableOutputs.find(o => o.name === outputName);
      if (!output) { setConnectedOutputAttributes(prev => ({ ...prev, [index]: { id: null, attributes: [] } })); return; }
      setConnectedOutputAttributes(prev => ({ ...prev, [index]: { id: output.index, attributes: output.attributes || [] } }));
    } catch (error) { setConnectedOutputAttributes(prev => ({ ...prev, [index]: { id: null, attributes: [] } })); }
  };

  const handleUnitShow = async () => {
    try {
      const res = await fetcher([`/new/workflow-app/measurement/unit/show/`]);
      let allUnits = [];
      if (res) res.forEach(item => allUnits.push(item));
      setAvailableMeasurementUnits(allUnits);
    } catch (error) { setAvailableUnits([]); enqueueSnackbar("Failed to fetch units", { variant: "error" }); }
  };

  useEffect(() => { fetchUnits(); handleUnitShow(); fetchAttributes(); fetchNodeAttributes(); fetchAvailableOutputs(); }, [stableRawMaterialInputs, stableNodeInputs, savedOutputIds, finalproduct]);
  useEffect(() => { outputs.forEach((output, index) => { if (output.isConnected === "yes" && output.connectedOutput && index > 0) fetchConnectedOutputAttributes(output.connectedOutput, index); else setConnectedOutputAttributes(prev => ({ ...prev, [index]: { id: null, attributes: [] } })); }); }, [outputs, availableOutputs]);

  useEffect(() => {
    if (!open) return;
    const initializeOutputs = async () => {
      let initialOutputs = [];
      let initialOutputIds = [];
      if (predefinedoutput?.length > 0) {
        try {
          const fetchedOutputs = await Promise.all(predefinedoutput.map(async (id) => {
            const res = await fetcher([`/new/workflow-app/product-outputs/${id}/`]);
            if (!res?.id) return null;
            return { id: res.id, outputName: res.name || "", name: getRawFormulaString(res.formula) || "", unit: res.unit || "", description: res.description || "", quantity: res.value?.quantity || 0, isConnected: res.parent_output ? "yes" : "no", connectedOutput: "", inputForNextProcess: res.input_for_next_process ? "yes" : "no", question: res.question || "", linked_attributes: res.linked_attributes || [], measurement_unit: res.measurement_unit || null };
          }));
          initialOutputs = fetchedOutputs.filter(Boolean);
          initialOutputIds = initialOutputs.map(o => o.id);
          if (initialOutputs.length > 0) setquestion(initialOutputs[0].question || "");
        } catch (err) { enqueueSnackbar("Could not load output data", { variant: "error" }); }
      } else if (editingOutput) {
        if (Array.isArray(editingOutput)) {
          initialOutputs = editingOutput.map(output => ({ id: output.id, outputName: output.name || "", name: getRawFormulaString(output.formula) || "", unit: output.unit || "", description: output.description || "", quantity: output.value?.quantity || 0, isConnected: output.parent_output ? "yes" : "no", connectedOutput: "", inputForNextProcess: output.input_for_next_process ? "yes" : "no", question: output.question || "", linked_attributes: output.linked_attributes || [], measurement_unit: output.measurement_unit || null }));
          initialOutputIds = editingOutput.map(o => o.id);
          setquestion(editingOutput[0]?.question || "");
        } else {
          initialOutputs = [{ id: editingOutput.id, outputName: editingOutput.name || "", name: getRawFormulaString(editingOutput.formula) || "", unit: editingOutput.unit || "", description: editingOutput.description || "", quantity: editingOutput.value?.quantity || 0, isConnected: editingOutput.parent_output ? "yes" : "no", connectedOutput: "", inputForNextProcess: editingOutput.input_for_next_process ? "yes" : "no", question: editingOutput.question || "", linked_attributes: editingOutput.linked_attributes || [], measurement_unit: editingOutput.measurement_unit || null }];
          initialOutputIds = [editingOutput.id];
          setquestion(editingOutput.question || "");
        }
      }
      if (initialOutputs.length > 0) { setOutputs(initialOutputs); setNumberOfOutputs(initialOutputs.length); setAllOutputIds(initialOutputIds); setSavedOutputIds(initialOutputIds); }
      else { setOutputs([{ name: "", outputName: "", unit: "", description: "", quantity: 0, isConnected: "no", connectedOutput: "", inputForNextProcess: "no", question: "", measurement_unit: "" }]); setNumberOfOutputs(1); setAllOutputIds([]); setSavedOutputIds([]); }
    };
    initializeOutputs();
  }, [open, editingOutput, outputids]);

  const handleNumberOfOutputsChange = (value) => {
    try {
      let num = Math.max(1, Math.min(10, parseInt(value) || 1));
      setNumberOfOutputs(num);
      const newOutputs = [...outputs];
      if (num > newOutputs.length) { for (let i = newOutputs.length; i < num; i++) newOutputs.push({ name: "", outputName: "", unit: "", description: "", quantity: 0, isConnected: "no", connectedOutput: "", inputForNextProcess: "no", question: "", measurement_unit: "" }); }
      else newOutputs.splice(num);
      setOutputs(newOutputs);
      setSelectedUnits(prev => { const n = [...(prev || [])]; if (num > n.length) for (let i = n.length; i < num; i++) n.push(null); else n.length = num; return n; });
      if (getAllAvailableLinkedAttributes.length > 0) { const newLA = []; for (let i = 0; i < num; i++) { if (i < linked_attributes.length) newLA[i] = linked_attributes[i]; else newLA[i] = getAllAvailableLinkedAttributes.map(attr => ({ productId: attr.productId, attribute: attr.attribute })); } setLinkedAttributes(newLA); }
      setSelectedNodeOutput(prev => { const ns = {}; for (let i = 0; i < num; i++) ns[i] = prev[i] || ""; return ns; });
    } catch (error) { enqueueSnackbar("Error updating number of outputs", { variant: "error" }); }
  };

  const handleOutputChange = (index, field, value) => {
    try {
      const newOutputs = [...outputs];
      if (field === "quantity") { const p = parseFloat(value); newOutputs[index][field] = Number.isNaN(p) || p < 0 ? 0 : p; }
      else newOutputs[index][field] = value;
      if (field === "isConnected" && value === "no") { newOutputs[index].connectedOutput = ""; setConnectedOutputAttributes(prev => ({ ...prev, [index]: { id: null, attributes: [] } })); }
      setOutputs(newOutputs);
    } catch (error) { enqueueSnackbar("Error updating output", { variant: "error" }); }
  };

  const handleFormulaChange = (index, value) => {
    const newOutputs = [...outputs];
    newOutputs[index].name = value;
    setOutputs(newOutputs);
  };

  const handleLinkedAttributesChange = (index, value) => {
    try {
      const newLA = [...linked_attributes];
      newLA[index] = value.map(val => { const [productId, attribute] = val.split(":"); return { productId: Number(productId), attribute }; });
      setLinkedAttributes(newLA);
    } catch (error) { enqueueSnackbar("Error updating linked attributes", { variant: "error" }); }
  };

  const parseFormulaToPayload = (formulaStr, isConnected) => {
    try {
      // Normalize spaces around operators first
      let normalized = formulaStr
        .replace(/([+\-*/()])/g, " $1 ") // Add spaces around operators and parentheses
        .replace(/\s+/g, " ") // Collapse multiple spaces
        .trim();

      if (!normalized) return { operation: "", operands: [], formulaStr, product: [], nodes: [], product_key: {} };

      const operatorMap = { "+": "add", "-": "subtract", "*": "multiply", "/": "divide" };
      const precedence = { "+": 1, "-": 1, "*": 2, "/": 2 };

      // Updated regex to properly handle multi-digit numbers and decimals
      // Also handle transformation tokens in format: t_instanceKey_productId_attribute
      const tokens = normalized.match(/(MAX|MIN|AVG|SUM|ROUND|ABS|POW|SQRT|\(|\)|t_\d+_\d+_\w+|t\.\d+\.\w+|o\.\d+|\d+\.\w+|-?\d+(?:\.\d+)?|[+\-*/])/g) || [];

      if (tokens.length === 0) return { operation: "", operands: [], formulaStr, product: [], nodes: [], product_key: {} };

      const usedProductIds = new Set();
      const usedNodeIds = new Set();
      const usedTransformationIds = new Set();

      const parseExpression = (tokenList, minPrecedence = 0) => {
        let left = parseAtom(tokenList);
        while (tokenList.length > 0 && tokenList[0] in operatorMap && precedence[tokenList[0]] >= minPrecedence) {
          const op = tokenList.shift();
          left = { operation: operatorMap[op], operands: [left, parseExpression(tokenList, precedence[op] + 1)] };
        }
        return left;
      };

      const parseAtom = (tokenList) => {
        if (!tokenList.length) throw new Error("Unexpected end of formula");

        // Handle functions
        const functionNames = ["MAX", "MIN", "AVG", "SUM", "ROUND", "ABS", "POW", "SQRT"];
        if (functionNames.includes(tokenList[0])) {
          const funcName = tokenList.shift();
          if (!tokenList.length || tokenList[0] !== "(") throw new Error(`Expected '(' after ${funcName}`);
          tokenList.shift();

          const args = [];
          if (tokenList.length && tokenList[0] !== ")") {
            args.push(parseExpression(tokenList));
            while (tokenList.length && tokenList[0] === ",") {
              tokenList.shift();
              args.push(parseExpression(tokenList));
            }
          }

          if (!tokenList.length || tokenList[0] !== ")") throw new Error(`Expected ')' after ${funcName} arguments`);
          tokenList.shift();

          return { operation: funcName.toLowerCase(), operands: args };
        }

        // Handle parentheses
        if (tokenList[0] === "(") {
          tokenList.shift();
          const expr = parseExpression(tokenList);
          if (!tokenList.length || tokenList[0] !== ")") throw new Error("Unmatched parenthesis");
          tokenList.shift();
          return expr;
        }

        const token = tokenList.shift();

        // Handle selected transformation attributes in format: t_instanceKey_productId_attribute
        if (token.match(/^t_\d+_\d+_\w+$/)) {
          const parts = token.split("_");
          const productId = parseInt(parts[2]);
          const attribute = parts[3];
          usedProductIds.add(productId);
          // Return the token as-is - it will be stored in the formula
          return token;
        }

        // Handle transform attributes (standard format: t.id.attr)
        if (token.match(/^t\.\d+\.\w+$/)) {
          const parts = token.split(".");
          usedTransformationIds.add(parseInt(parts[1]));
          return token;
        }

        // Handle node outputs
        if (token.match(/^o\.\d+$/)) {
          usedNodeIds.add(parseInt(token.split(".")[1]));
          return token;
        }

        // Handle material attributes
        if (token.match(/^\d+\.\w+$/)) {
          const lastDot = token.lastIndexOf(".");
          const matId = parseFloat(token.substring(0, lastDot));
          if (!Number.isNaN(matId)) {
            usedProductIds.add(matId);
            return token;
          }
          return token;
        }

        // Handle numbers (integers and decimals)
        if (token.match(/^-?\d+(?:\.\d+)?$/)) {
          return parseFloat(token);
        }

        throw new Error(`Unknown token: ${token}`);
      };

      const tokensCopy = [...tokens];
      const parsed = parseExpression(tokensCopy);

      // Ensure parsed always has operation and operands structure
      const result = typeof parsed === 'object' && parsed !== null && 'operation' in parsed
        ? parsed
        : { operation: "", operands: [parsed], formulaStr };

      return {
        ...result,
        formulaStr,
        product: Array.from(usedProductIds),
        nodes: Array.from(usedNodeIds),
        transformation_outputs: Array.from(usedTransformationIds),
        product_key: {}
      };
    } catch (error) {
      console.error("Parse error:", error);
      enqueueSnackbar(`Invalid formula: ${error.message}`, { variant: "error" });
      return { operation: "", operands: [], formulaStr, product: [], nodes: [], product_key: {} };
    }
  };

  const handleSaveOutput = async (index) => {
    try {
      const output = outputs[index];
      if (!output.outputName.trim()) {
        enqueueSnackbar(`Output Name is required for Output ${index + 1}`, { variant: "error" });
        return;
      }
      if (output.isConnected === "yes" && !output.connectedOutput) {
        enqueueSnackbar(`Please select a connected output or change to "No"`, { variant: "error" });
        return;
      }

      const formula = parseFormulaToPayload(output.name, output.isConnected);
      let currentLinkedAttributes = linked_attributes[index] || [];

      if (output.isConnected === "yes" && output.connectedOutput) {
        const connectedOutput = availableOutputs.find(o => o.name === output.connectedOutput);
        if (connectedOutput) {
          try {
            const connectedOutputData = await fetcher([`/new/workflow-app/product-outputs/${connectedOutput.index}/`]);
            if (connectedOutputData?.linked_attributes) {
              const combinedMap = new Map();
              connectedOutputData.linked_attributes.forEach(attr => combinedMap.set(`${attr.productId}:${attr.attribute}`, attr));
              currentLinkedAttributes.forEach(attr => combinedMap.set(`${attr.productId}:${attr.attribute}`, attr));
              currentLinkedAttributes = Array.from(combinedMap.values());
            }
          } catch (e) {
            enqueueSnackbar("Failed to fetch connected output attributes", { variant: "warning" });
          }
        }
      }

      if (index > 0) {
        const previousLinkedAttributes = [];
        for (let i = 0; i < index; i++) {
          if (outputs[i]?.id) {
            try {
              const prevData = await fetcher([`/new/workflow-app/product-outputs/${outputs[i].id}/`]);
              if (prevData?.linked_attributes) previousLinkedAttributes.push(...prevData.linked_attributes);
            } catch (e) {
              console.error(e);
            }
          }
          else if (linked_attributes[i]) previousLinkedAttributes.push(...linked_attributes[i]);
        }
        if (previousLinkedAttributes.length > 0) {
          const combinedMap = new Map();
          previousLinkedAttributes.forEach(attr => combinedMap.set(`${attr.productId}:${attr.attribute}`, attr));
          currentLinkedAttributes.forEach(attr => combinedMap.set(`${attr.productId}:${attr.attribute}`, attr));
          currentLinkedAttributes = Array.from(combinedMap.values());
        }
      }

      // Check if we have options (affectedoutput case)
      const hasOptions = affectedoutput && optionConfig?.options?.length > 0;

      // Build transformation data - NOW SUPPORTS OPTIONS
      let transformationData = null;

      if (hasOptions && output.optionFormulas) {
        // For each option, check if it has its own transformation settings
        const optionTransformations = {};
        let hasAnyTransformation = false;

        optionConfig.options.forEach(option => {
          const instanceKey = `${index}_${option.id}`;
          const needsTransform = needsTransformation[instanceKey] === "yes";
          const hasSelectedAttributes = selectedTransformAttributes[instanceKey]?.length > 0;

          if (needsTransform && hasSelectedAttributes) {
            hasAnyTransformation = true;
            optionTransformations[option.label] = {
              attributes: selectedTransformAttributes[instanceKey],
              operators: transformOperators[instanceKey] || {},
              userValues: transformUserValues[instanceKey] || {}
            };
          }
        });

        if (hasAnyTransformation) {
          transformationData = {
            type: "per_option",
            options: optionTransformations
          };
        }
      } else {
        // Single formula case - use simple transformation
        const needsTransform = needsTransformation[index] === "yes";
        const hasSelectedAttributes = selectedTransformAttributes[index]?.length > 0;

        if (needsTransform && hasSelectedAttributes) {
          transformationData = {
            type: "single",
            attributes: selectedTransformAttributes[index],
            operators: transformOperators[index] || {},
            userValues: transformUserValues[index] || {}
          };
        }
      }

      const outputData = {
        name: output.outputName,
        value: { quantity: output.quantity },
        question: question || "",
        linked_attributes: currentLinkedAttributes,
        unit: output.unit || "",
        description: output.description || "",
        input_for_next_process: output.inputForNextProcess === "yes",
        parent_output: output.isConnected === "yes" ? availableOutputs.find(o => o.name === output.connectedOutput)?.index || null : null,
        product_variant: [],
        product_key: {},
        measurement_unit: output.measurement_unit,
        ...(affectedoutput && output.optionFormulas ? {
          option_formulas: Object.fromEntries(
            Object.entries(output.optionFormulas).map(([optionId, formulaStr]) => {
              const option = optionConfig?.options?.find(o => o.id === optionId);
              const parsedFormula = parseFormulaToPayload(formulaStr, output.isConnected);

              // Get transformation for this specific option
              const instanceKey = `${index}_${optionId}`;
              const optionTransformation = (needsTransformation[instanceKey] === "yes" && selectedTransformAttributes[instanceKey]?.length > 0)
                ? {
                  attributes: selectedTransformAttributes[instanceKey],
                  operators: transformOperators[instanceKey] || {},
                  userValues: transformUserValues[instanceKey] || {}
                }
                : null;

              return [option?.label ?? optionId, {
                operation: parsedFormula.operation || "unknown",
                operands: Array.isArray(parsedFormula.operands) && parsedFormula.operands.length > 0
                  ? parsedFormula.operands
                  : [formulaStr],
                formulaStr: formulaStr,
                // Include transformation for this specific option
                ...(optionTransformation && { transformation: optionTransformation })
              }];
            })
          )
        } : {}),
        // Add transformation data if it exists for single formula case
        ...(transformationData && !hasOptions && { transformation: transformationData })
      };

      if (formula?.operands?.length > 0 && formula.operation) {
        outputData.formula = {
          operation: formula.operation,
          operands: formula.operands,
          formulaStr: output.name.trim()
        };
      } else {
        outputData.formula = null;
      }

      if (formula.product?.length > 0) outputData.product = formula.product;
      if (formula.nodes?.length > 0) outputData.nodes = formula.nodes;
      if (formula.transformation_outputs?.length > 0) outputData.transformation_outputs = formula.transformation_outputs;

      let res;
      let isExistingOutput = false;
      let outputId = null;

      if (editingOutput && index === 0) {
        outputId = editingOutput[0].id;
        isExistingOutput = true;
      }
      else if (output.id) {
        outputId = output.id;
        isExistingOutput = true;
      }

      setOutputPayload((prev) => ({ ...prev, outputData }));

      if (isExistingOutput && outputId && predefined === false) {
        res = await patchFetcher([`/new/workflow-app/product-outputs/${outputId}/`, outputData]);
        if (res?.id) {
          const uo = [...outputs];
          uo[index].id = res.id;
          setOutputs(uo);
          setAllOutputIds(prev => prev.includes(res.id) ? prev : [...prev, res.id]);
          setSavedOutputIds(prev => prev.includes(res.id) ? prev : [...prev, res.id]);
          await fetchAvailableOutputs();
          enqueueSnackbar(`Output "${output.outputName}" updated successfully!`, { variant: "success" });
        }
      } else {
        res = await postFetcher(["/new/workflow-app/product-outputs/", outputData]);
        if (res?.id) {
          const uo = [...outputs];
          uo[index].id = res.id;
          setOutputs(uo);
          setAllOutputIds(prev => [...prev, res.id]);
          setSavedOutputIds(prev => [...prev, res.id]);
          await fetchAvailableOutputs();
          enqueueSnackbar(`Output ${index + 1} created successfully`, { variant: "success" });
        }
      }

      if (res?.id) {
        setLastPostedIndex(index);
        if (selectedNodeOutput[index]) {
          const so = nodeOutputs.flatMap(n => n.outputs).find(o => o.name === selectedNodeOutput[index]);
          if (so) onSave({ outputId: so.id, outputName: so.name, label: so.name });
        }
        if (index === outputs.length - 1) {
          if (onUnitsSelected) onUnitsSelected(selectedUnits.filter(u => u !== null));
          enqueueSnackbar("All changes saved!", { variant: "success" });
          setCurrentTab(0);
          handleClose();
        }
      } else {
        enqueueSnackbar("Failed to save output.", { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar(`Failed to save: ${error.message || "Unknown error"}`, { variant: "error" });
    }
  };

  // ── Transformation handlers ────────────────────────────────────────────────
  // Update the transformation state handlers to work with option-specific keys
  const handleTransformationChange = (instanceKey, value) => {
    setNeedsTransformation(prev => ({ ...prev, [instanceKey]: value }));
    if (value === "no") {
      setSelectedTransformAttributes(prev => ({ ...prev, [instanceKey]: [] }));
      setTransformOperators(prev => ({ ...prev, [instanceKey]: {} }));
      setTransformUserValues(prev => ({ ...prev, [instanceKey]: {} }));
    }
  };

  const handleTransformAttributeToggle = (instanceKey, productId, attribute) => {
    setSelectedTransformAttributes(prev => {
      const current = prev[instanceKey] || [];
      const key = `${productId}:${attribute}`;
      const newSelection = current.includes(key) ? current.filter(k => k !== key) : [...current, key];

      // Initialize operators and values for new selections
      const updatedOperators = { ...(transformOperators[instanceKey] || {}) };
      const updatedValues = { ...(transformUserValues[instanceKey] || {}) };

      newSelection.forEach(k => {
        if (!updatedOperators[k]) updatedOperators[k] = "+";
        if (updatedValues[k] === undefined) updatedValues[k] = "";
      });

      // Clean up removed selections
      Object.keys(updatedOperators).forEach(k => {
        if (!newSelection.includes(k)) {
          delete updatedOperators[k];
          delete updatedValues[k];
        }
      });

      setTransformOperators(prev => ({ ...prev, [instanceKey]: updatedOperators }));
      setTransformUserValues(prev => ({ ...prev, [instanceKey]: updatedValues }));

      return { ...prev, [instanceKey]: newSelection };
    });
  };

  const handleTransformOperatorChange = (instanceKey, attributeKey, operator) => {
    setTransformOperators(prev => ({
      ...prev,
      [instanceKey]: { ...(prev[instanceKey] || {}), [attributeKey]: operator }
    }));
  };

  const handleTransformUserValueChange = (instanceKey, attributeKey, value) => {
    setTransformUserValues(prev => ({
      ...prev,
      [instanceKey]: { ...(prev[instanceKey] || {}), [attributeKey]: value }
    }));
  };

  const handleClose = () => {
    try {
      setNumberOfOutputs(1);
      setOutputs([{ name: "", outputName: "", unit: "", description: "", quantity: 0, isConnected: "no", connectedOutput: "", inputForNextProcess: "no" }]);
      setLastPostedIndex(-1); setConnectedOutputAttributes({}); setAvailableOutputs({}); setSelectedNodeOutput({}); setNodeOutputs([]); setLinkedAttributes([]);
      setNeedsTransformation({}); setSelectedTransformAttributes({}); setTransformOperators({}); setTransformUserValues({});
      onClose();
    } catch (error) { enqueueSnackbar("Error closing dialog", { variant: "error" }); }
  };

  useEffect(() => { if (nodes.length > 0 && open) fetchTransformAttributes(); }, [nodes, open]);

  // ── Render output tab ─────────────────────────────────────────────────────
  const renderOutputTab = (output, index) => {
    const isExistingOutput = output?.id !== undefined;
    const isDisabled = !isExistingOutput && index > lastPostedIndex + 1;
    const finalProduct = finalproduct && Array.isArray(finalproduct) && finalproduct.length > 0 ? finalproduct[0] : null;
    const finalProductAttributes = finalProduct ? attributesList.find(m => m.id === finalProduct.id)?.attributes || [] : [];
    const allAttributesList = attributesList;
    const currentFormula = affectedoutput && focusedOptionId
      ? output.optionFormulas?.[focusedOptionId]
      : output.name;
    // Build all material attributes for transformation
    const allMaterialAttributes = [];
    attributesList.forEach(mat => mat.attributes.forEach(attr => allMaterialAttributes.push({ productId: mat.id, productName: mat.name, attribute: attr, displayName: `${mat.name}: ${attr}` })));

    // ── RIGHT PANEL: clickable attribute palette ─────────────────────────────


    const hasOptions = affectedoutput && optionConfig?.options?.length > 0;
    return (
      <Box sx={{ opacity: isDisabled ? 0.5 : 1, pointerEvents: isDisabled ? "none" : "auto" }}>
        {/* ── Two-column layout ── */}
        <Box sx={{ display: "flex", gap: 0, minHeight: 0 }}>

          {/* ══ LEFT COLUMN ══ */}
          <Box sx={{ flex: "0 0 60%", p: 4, overflowY: "auto", maxHeight: "calc(100vh - 240px)", borderRight: `1.5px solid ${c.border}`, background: c.bg }}>

            {/* Output name + unit */}
            <Box sx={{ ...sectionStyle }}>
              <SectionHeader label="Output Details" />
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  label="Output Name *"
                  value={output.outputName}
                  onChange={e => handleOutputChange(index, "outputName", e.target.value)}
                  fullWidth size="small" sx={{ ...fieldSx, mb: 0 }}
                  placeholder="e.g., Cut Sheet" disabled={isDisabled}
                />
                <FormControl size="small" sx={{ minWidth: 160, flexShrink: 0, "& .MuiOutlinedInput-root": { borderRadius: "10px", "& fieldset": { borderColor: c.border } } }}>
                  <InputLabel sx={{ fontSize: "13px" }}>Unit</InputLabel>
                  <Select value={selectedUnits[index]?.id || ""} onChange={e => {
                    const unitObj = availableMeasurementUnits.find(u => u.id === e.target.value);
                    setSelectedUnits(prev => { const up = [...prev]; up[index] = unitObj ? { id: unitObj.id, name: unitObj.name } : null; return up; });
                    handleOutputChange(index, "measurement_unit", e.target.value);
                  }} label="Unit" disabled={isDisabled}>
                    <MenuItem value=""><em>None</em></MenuItem>
                    {availableMeasurementUnits.map(unit => <MenuItem key={unit.id} value={unit.id} sx={{ fontSize: "13px" }}>{unit.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Formula Builder */}
            {nodeType !== "diamond" && (
              <Box sx={{ ...sectionStyle }}>
                <SectionHeader label="Formula Builder" />
                {affectedoutput && optionConfig?.options?.length > 0 ? (
                  optionConfig.options.map(option => (
                    <Box key={option.id} sx={{ mb: 2.5 }}>
                      <Typography sx={{ fontSize: "11px", fontWeight: 700, color: c.textSecondary, mb: 0.75, letterSpacing: "0.06em", textTransform: "uppercase" }}>Formula for: {option.label}</Typography>
                      <FormulaBuilder
                        value={output.optionFormulas?.[option.id] ?? ""}
                        onChange={val => { const n = [...outputs]; n[index].optionFormulas = { ...(n[index].optionFormulas || {}), [option.id]: val }; setOutputs(n); setFocusedOptionId(option.id); }}
                        attributes={allAttributesList} nodeOutputs={filteredNodeOutputs} transformAttributes={transformAttributes} connectedOutputAttributes={connectedOutputAttributes[index]}
                        disabled={isDisabled} placeholder={`Build formula for "${option.label}"…`}
                      />
                      <FormulaPreview formulaStr={output.optionFormulas?.[option.id] ?? ""} attributes={allAttributesList} nodeOutputs={filteredNodeOutputs} />
                    </Box>
                  ))
                ) : (
                  <>
                    <FormulaBuilder value={output.name} onChange={val => handleFormulaChange(index, val)} attributes={allAttributesList} nodeOutputs={filteredNodeOutputs} transformAttributes={transformAttributes} connectedOutputAttributes={connectedOutputAttributes[index]} disabled={isDisabled} />
                    <FormulaPreview formulaStr={output.name} attributes={allAttributesList} nodeOutputs={filteredNodeOutputs} />
                  </>
                )}
              </Box>
            )}

            {/* Linked Attributes */}
            {nodeType !== "diamond" && getAllAvailableLinkedAttributes.length > 0 && (
              <Box sx={{ ...sectionStyle }}>
                <SectionHeader label="Linked Attributes" badge={linked_attributes[index]?.length || 0} />
                <FormControl fullWidth size="small" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", } }}>
                  <InputLabel sx={{ fontSize: "13px" }}>Select Linked Attributes</InputLabel>
                  <Select multiple value={linked_attributes[index]?.map(a => `${a.productId}:${a.attribute}`) || []} onChange={e => handleLinkedAttributesChange(index, e.target.value)} label="Select Linked Attributes" disabled={isDisabled}
                    renderValue={selected => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map(value => { const [pid, attr] = value.split(":"); const info = getAllAvailableLinkedAttributes.find(a => a.productId === Number(pid) && a.attribute === attr); return <Chip key={value} label={info?.displayName || `${pid}.${attr}`} size="small" sx={{ borderRadius: "4px", fontSize: "11px" }} onDelete={e => { e.stopPropagation(); const cv = linked_attributes[index]?.map(a => `${a.productId}:${a.attribute}`) || []; handleLinkedAttributesChange(index, cv.filter(v => v !== value)); }} />; })}</Box>
                    )}>
                    <MenuItem disabled sx={{ fontSize: "11px", fontWeight: 700, color: c.textSecondary }}>— Raw Materials —</MenuItem>
                    {getAllAvailableLinkedAttributes.filter(a => a.type === "material").map(a => (
                      <MenuItem key={`${a.productId}-${a.attribute}`} value={`${a.productId}:${a.attribute}`} sx={{ fontSize: "13px" }}>
                        <Checkbox checked={linked_attributes[index]?.some(la => la.productId === a.productId && la.attribute === a.attribute) || false} size="small" />
                        <ListItemText primary={a.displayName} primaryTypographyProps={{ fontSize: "13px" }} />
                      </MenuItem>
                    ))}
                    <MenuItem disabled sx={{ fontSize: "11px", fontWeight: 700, color: c.textSecondary }}>— Node Outputs —</MenuItem>
                    {getAllAvailableLinkedAttributes.filter(a => a.type === "node_output").map(a => (
                      <MenuItem key={`${a.productId}-${a.attribute}-${a.outputName}`} value={`${a.productId}:${a.attribute}`} sx={{ fontSize: "13px" }}>
                        <Checkbox checked={linked_attributes[index]?.some(la => la.productId === a.productId && la.attribute === a.attribute) || false} size="small" />
                        <ListItemText primary={a.displayName} primaryTypographyProps={{ fontSize: "13px" }} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Description */}
            {nodeType !== "diamond" && (
              <Box sx={{ ...sectionStyle }}>
                <SectionHeader label="Description" />
                <TextField value={output.description} onChange={e => handleOutputChange(index, "description", e.target.value)} fullWidth size="small" sx={{ ...fieldSx, mb: 0 }} multiline rows={2} placeholder="Optional: describe this output…" disabled={isDisabled} />
              </Box>
            )}

            {/* Action buttons */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1, gap: 1.5 }}>
              {output.id && (
                <Button variant="outlined" onClick={() => handleDeleteOutput(index)} disabled={isDisabled}
                  sx={{ borderRadius: "10px", borderColor: "#FECDD2", color: c.error, fontSize: "13px", fontWeight: 600, px: 3, "&:hover": { background: c.errorBg, borderColor: c.error } }}>
                  Delete
                </Button>
              )}
              <Button onClick={() => handleSaveOutput(index)} variant="contained" disabled={isDisabled || !output.outputName.trim()}
                sx={{ borderRadius: "10px", background: c.accent, color: "#fff", fontSize: "13px", fontWeight: 700, boxShadow: "none", px: 4, height: 42, "&:hover": { background: c.accentHover, boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }, "&:disabled": { background: "#E2E8F0", color: "#94A3B8" } }}>
                {output.id ? "Update Output" : "Save Output"} {index + 1}
              </Button>
            </Box>
          </Box>

          {/* ══ RIGHT COLUMN ══ */}
          <Box sx={{ flex: "0 0 40%", p: 4, overflowY: "auto", maxHeight: "calc(100vh - 240px)", background: "#FFFFFF", borderLeft: `1.5px solid ${c.border}` }}>

            {/* Attributes Palette - Always show this */}
            <RightPanel
              formulaValue={currentFormula}
              attributesList={allAttributesList}
              nodeOutputs={filteredNodeOutputs}
              transformAttributes={transformAttributes}
              onTokenClick={(token) => {
                const newOutputs = [...outputs];
                if (hasOptions && focusedOptionId) {
                  const cur = (newOutputs[index].optionFormulas?.[focusedOptionId] || "").trim();
                  newOutputs[index].optionFormulas = {
                    ...(newOutputs[index].optionFormulas || {}),
                    [focusedOptionId]: cur ? `${cur} ${token}` : token
                  };
                } else {
                  newOutputs[index].name = newOutputs[index].name.trim() ? `${newOutputs[index].name.trim()} ${token}` : token;
                }
                setOutputs(newOutputs);
              }}
              onInsertOperator={(operator) => {
                const newOutputs = [...outputs];
                if (hasOptions && focusedOptionId) {
                  const cur = (newOutputs[index].optionFormulas?.[focusedOptionId] || "").trim();
                  newOutputs[index].optionFormulas = {
                    ...(newOutputs[index].optionFormulas || {}),
                    [focusedOptionId]: cur ? `${cur} ${operator}` : operator
                  };
                } else {
                  newOutputs[index].name = newOutputs[index].name.trim() ? `${newOutputs[index].name.trim()} ${operator}` : operator;
                }
                setOutputs(newOutputs);
              }}
              onInsertFunction={(func) => {
                const newOutputs = [...outputs];
                const token = ["MAX", "MIN", "AVG", "SUM"].includes(func) ? `${func}(` : func;
                if (hasOptions && focusedOptionId) {
                  const cur = (newOutputs[index].optionFormulas?.[focusedOptionId] || "").trim();
                  newOutputs[index].optionFormulas = {
                    ...(newOutputs[index].optionFormulas || {}),
                    [focusedOptionId]: cur ? `${cur} ${token}` : token
                  };
                } else {
                  newOutputs[index].name = newOutputs[index].name.trim() ? `${newOutputs[index].name.trim()} ${token}` : token;
                }
                setOutputs(newOutputs);
              }}
              nodeType={nodeType}
              allMaterialAttributes={allMaterialAttributes}
              needsTransformation={needsTransformation}
              selectedTransformAttributes={selectedTransformAttributes}
              transformOperators={transformOperators}
              transformUserValues={transformUserValues}
              onTransformationChange={handleTransformationChange}
              onAttributeToggle={handleTransformAttributeToggle}
              onOperatorChange={handleTransformOperatorChange}
              onValueChange={handleTransformUserValueChange}
              index={index}
              output={output}
              handleOutputChange={handleOutputChange}
              availableOutputs={availableOutputs}
              connectedOutputAttributes={connectedOutputAttributes}
              isDisabled={isDisabled}
              finalProduct={finalProduct}
              finalProductAttributes={finalProductAttributes}
              filteredNodeOutputs={filteredNodeOutputs}
              affectedoutput={affectedoutput}
              focusedOptionId={focusedOptionId}
              setOutputs={setOutputs}
              outputs={outputs}
              hasOptions={hasOptions} // Pass this down
              optionConfig={optionConfig} // Pass option config
            />
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Dialog
        open={open} onClose={handleClose} maxWidth="xl" fullWidth
        PaperProps={{ sx: { borderRadius: "24px", background: c.bg, boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)", overflow: "hidden" } }}
      >
        {/* Title bar */}
        <DialogTitle sx={{ background: c.surface, borderBottom: `1px solid ${c.border}`, px: 4, py: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: "10px", background: c.accent, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Typography sx={{ color: "#fff", fontSize: "14px" }}>⊕</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "15px", color: c.text, lineHeight: 1.2 }}>
                {editingOutput ? "Edit Outputs" : "Add Outputs"}
              </Typography>
              <Typography sx={{ fontSize: "11px", color: c.textMuted }}>
                {outputs.filter(o => o.id).length} of {outputs.length} saved
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClose} size="small" sx={{ borderRadius: "10px", "&:hover": { background: c.accentLight } }}>
            <Typography sx={{ fontSize: "16px", color: c.textSecondary, lineHeight: 1 }}>✕</Typography>
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0, background: c.bg, borderColor: c.border }}>
          {/* Controls row */}
          <Box sx={{ px: 4, pt: 3, pb: 0, background: c.surface, borderBottom: `1px solid ${c.border}` }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: "13px", fontWeight: 700, color: c.textSecondary, whiteSpace: "nowrap" }}>Outputs:</Typography>
                <Box sx={{ display: "flex", alignItems: "center", border: `1.5px solid ${c.border}`, borderRadius: "10px", overflow: "hidden" }}>
                  <IconButton size="small" onClick={() => handleNumberOfOutputsChange(numberOfOutputs - 1)} disabled={numberOfOutputs <= 1} sx={{ borderRadius: 0, px: 1, py: 0.5, "&:hover": { background: c.accentLight } }}>
                    <Typography sx={{ fontSize: "16px", lineHeight: 1, color: numberOfOutputs <= 1 ? c.textMuted : c.text }}>−</Typography>
                  </IconButton>
                  <Typography sx={{ px: 2, fontSize: "14px", fontWeight: 700, color: c.text, minWidth: 28, textAlign: "center" }}>{numberOfOutputs}</Typography>
                  <IconButton size="small" onClick={() => handleNumberOfOutputsChange(numberOfOutputs + 1)} disabled={numberOfOutputs >= 10} sx={{ borderRadius: 0, px: 1, py: 0.5, "&:hover": { background: c.accentLight } }}>
                    <Typography sx={{ fontSize: "16px", lineHeight: 1, color: numberOfOutputs >= 10 ? c.textMuted : c.text }}>+</Typography>
                  </IconButton>
                </Box>
              </Box>
              {nodeType === "diamond" && (
                <TextField label="Decision Question" value={question || ""} onChange={e => setquestion(e.target.value)} size="small" sx={{ flex: 1, ...fieldSx, mb: 0 }} placeholder="e.g., Is quality acceptable?" />
              )}
            </Box>
            <Tabs
              value={currentTab}
              onChange={(_, v) => setCurrentTab(v)}
              variant="scrollable" scrollButtons="auto"
              sx={{
                "& .MuiTab-root": { fontSize: "13px", fontWeight: 600, color: c.textSecondary, textTransform: "none", minHeight: 44, borderRadius: "10px 10px 0 0", "&.Mui-selected": { color: c.text } },
                "& .MuiTabs-indicator": { background: c.borderFocus, height: "3px", borderRadius: "3px 3px 0 0" },
              }}
            >
              {outputs.map((_, index) => (
                <Tab
                  key={index}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      {outputs[index]?.id ? (
                        <Iconify icon="solar:check-circle-bold" width={16} sx={{ color: c.success }} />
                      ) : (
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: c.border }} />
                      )}
                      Output {index + 1}
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Box>

          {/* Tab content */}
          {outputs.map((output, index) => (
            <div key={index} style={{ display: currentTab === index ? "block" : "none" }}>
              {renderOutputTab(output, index)}
            </div>
          ))}
        </DialogContent>

        <DialogActions sx={{ background: c.surface, borderTop: `1px solid ${c.border}`, px: 4, py: 2.5 }}>
          <Button onClick={handleClose} variant="outlined" sx={{ borderRadius: "10px", borderColor: c.border, color: c.textSecondary, fontSize: "14px", fontWeight: 600, px: 4, "&:hover": { borderColor: "#94A3B8", background: c.bg } }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <OutputDeleteConfirmationDialog
        key={`delete-dialog-${directLinks.length}-${directNodes.length}-${downstreamLinks.length}-${downstreamNodes.length}`}
        open={deleteConfirmOpen} onClose={handleClosedelete} onConfirm={confirmDelete}
        directLinks={directLinks} downstreamLinks={downstreamLinks} directNodes={directNodes} downstreamNodes={downstreamNodes}
        selectedLinks={selectedLinks} setSelectedLinks={setSelectedLinks} selectedNodes={selectedNodes} setSelectedNodes={setSelectedNodes}
        setdeleteeditDialog={setdeleteeditDialog}
      />

      <AddUnitDialog open={showAddUnitDialog} onClose={() => setShowAddUnitDialog(false)} onRefresh={() => handleUnitShow()} onSave={unit => { setAvailableUnits(prev => [...prev, unit]); setShowAddUnitDialog(false); }} existingUnits={availableUnits} />

      <DeleteConfirmationInsideEdit
        open={showdeleteeditDialog} onClose={() => setdeleteeditDialog(false)}
        permanentdeleteeditnode={permanentdeleteeditnode} setpermanentdeleteeditnode={setpermanentdeleteeditnode}
        onSave={() => confirmDelete()}
      />
    </>
  );
};

export default AddOutputDialog;